import { useState, useEffect } from 'react';
import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  UserCheck,
  Users,
  Home,
  ShieldAlert,
  CheckCircle,
  CalendarDays,
  Edit,
  FileText,
  MapPin,
  Plus,
  Trash2,
  Eye,
  Percent,
  Star,
  Download,
  ChevronDown,
  ChevronRight,
  Shuffle,
  Flag,
  Newspaper,
  Send,
  X,
  RefreshCw,
  CreditCard,
  Search,
  Pin,
  PinOff,
  Code2,
  Copy,
  Check,
  DollarSign,
  PieChart,
  Ticket,
} from 'lucide-react';
import type { PartnerApplication, Creator, Class, ClassSession } from '@shared/schema';
import { CALENDAR_CATEGORIES } from '@shared/schema';
import type {
  AdminPageContentResponseDto,
  AdminPageContentSectionDto,
  AdminPromoCodeDto,
  AdminPromoCodeOrderDto,
  PaginatedResponse,
  AdminCreatorDto,
} from '@shared/api-contracts';

import { AdminClassManagement } from './class-dashboard';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { apiRequest, fetchJson, queryClient } from '@/lib/queryClient';
import { authedFetch, authedJson } from '@/lib/http';
import { PublicShell } from '@/components/public-shell';
import { formatDateCompact, formatDateWithTimezone } from '@/lib/dateUtils';
import { displayPhoneNumber } from '@/lib/phoneFormat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FinancialsOverview } from '@/components/admin/financials-overview';
import { PayoutsAdmin } from '@/components/admin/payouts-admin';
import { AmbassadorsAdmin } from '@/components/admin/ambassadors-admin';
import { PassProductsAdmin } from '@/components/admin/pass-products-admin';
import { PassHoldersManager } from '@/components/pass-holders-manager';
import {
  LedgerAdmin,
  ConnectedAccountsAdmin,
  AuditLogAdmin,
} from '@/components/admin/finance-extras';
import { AdminHome } from '@/components/admin/admin-home';
import { OrdersLookup } from '@/components/admin/orders-lookup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { BlogEditor } from '@/components/blog-editor';

type AdminCreatorRow = AdminCreatorDto;

interface AdminEmailAlert {
  code: string;
  severity: 'warning' | 'critical';
  title: string;
  message: string;
}

interface AdminEmailHealthSummary {
  deadLetterCount: number;
  deadLetterRecentCount: number;
  deadLetterWindowHours: number;
  deadLetterThreshold: number;
  activeAlerts: AdminEmailAlert[];
  usageByProvider: Array<{
    provider: string;
    rolling30ExternalRecipientCount: number;
    dailyExternalRecipientCount: number;
  }>;
}

interface DeadLetteredEmailRow {
  id: string;
  recipientEmail: string;
  templateKey: string;
  status: string;
  sendAttempts: number;
  lastError?: string | null;
  queuedAt: string;
  updatedAt: string;
}

interface SupportCheckoutRow {
  id: string;
  userEmail: string;
  userFirstName?: string | null;
  userLastName?: string | null;
  classTitle: string;
  sessionStartDateTime: string;
  sessionEndDateTime?: string | null;
  sessionMaxStudents?: number | null;
  sessionSeatHoldCount: number;
  status: string;
  quantity: number;
  promoCode?: string | null;
  seatHoldCount: number;
  expiresAt: string;
  updatedAt: string;
  orderId?: string | null;
  orderStatus?: string | null;
  orderNetAmountCents?: number | null;
  enrollmentCount: number;
}

// Helper function to convert UTC datetime to local datetime string for datetime-local input
function utcToLocalDatetimeString(utcDateString: string): string {
  const date = new Date(utcDateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const SELECT_ALL = '__all__';
const SELECT_NONE = '__none__';

// ─── Admin nav config ────────────────────────────────────────────────────────

type AdminSection = {
  value: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
};

type AdminNavGroup = {
  label: string;
  items: AdminSection[];
};

function buildNavGroups(pendingBlogCount: number, pendingCreatorApps: number): AdminNavGroup[] {
  return [
    {
      label: 'Overview',
      items: [{ value: 'home', label: 'Home', icon: <Home className="h-4 w-4" /> }],
    },
    {
      label: 'Content',
      items: [
        { value: 'content', label: 'Pages', icon: <FileText className="h-4 w-4" /> },
        {
          value: 'blog',
          label: 'Blog',
          icon: <Newspaper className="h-4 w-4" />,
          badge: pendingBlogCount || undefined,
        },
      ],
    },
    {
      label: 'Commerce',
      items: [
        { value: 'promo-codes', label: 'Promo Codes', icon: <Percent className="h-4 w-4" /> },
        { value: 'payments', label: 'Payments', icon: <DollarSign className="h-4 w-4" /> },
        { value: 'fee-profiles', label: 'Payout Splits', icon: <PieChart className="h-4 w-4" /> },
        { value: 'punch-passes', label: 'Punch Passes', icon: <Ticket className="h-4 w-4" /> },
        { value: 'orders', label: 'Orders', icon: <CreditCard className="h-4 w-4" /> },
        { value: 'refunds', label: 'Refunds', icon: <CheckCircle className="h-4 w-4" /> },
        { value: 'support', label: 'Support', icon: <CreditCard className="h-4 w-4" /> },
      ],
    },
    {
      label: 'Users',
      items: [
        { value: 'user-mgmt', label: 'Users & Classes', icon: <Users className="h-4 w-4" /> },
        {
          value: 'creator-apps',
          label: 'Creator Apps',
          icon: <Star className="h-4 w-4" />,
          badge: pendingCreatorApps || undefined,
        },
        { value: 'applications', label: 'Applications', icon: <UserCheck className="h-4 w-4" /> },
        { value: 'moderation', label: 'Moderation', icon: <Flag className="h-4 w-4" /> },
      ],
    },
    {
      label: 'Operations',
      items: [
        { value: 'email-ops', label: 'Email', icon: <Send className="h-4 w-4" /> },
        { value: 'calendar', label: 'Calendar', icon: <CalendarDays className="h-4 w-4" /> },
        { value: 'locations', label: 'Locations', icon: <MapPin className="h-4 w-4" /> },
        {
          value: 'kill-switches',
          label: 'Kill Switches',
          icon: <ShieldAlert className="h-4 w-4" />,
        },
        { value: 'audit', label: 'Audit Log', icon: <FileText className="h-4 w-4" /> },
      ],
    },
  ];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState(
    () => new URLSearchParams(window.location.search).get('section') || 'home'
  );
  const [selectedPage, setSelectedPage] = useState<string>('landing');
  const [editingContent, setEditingContent] = useState<AdminPageContentSectionDto | null>(null);
  const [contentForm, setContentForm] = useState({
    sectionKey: '',
    contentValue: '',
    contentType: 'text',
  });
  const [isAddingPromoCode, setIsAddingPromoCode] = useState(false);
  const [supportCheckoutSearch, setSupportCheckoutSearch] = useState('');
  const [supportCheckoutStatus, setSupportCheckoutStatus] = useState('active');
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
  });
  // Forms removed - not being used in current implementation

  // User Management State (simplified)
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
  });
  // User form removed - not used in current implementation

  // Partner Applications State
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [isViewingApplication, setIsViewingApplication] = useState(false);

  // Creator application expanded row state
  const [expandedCreatorAppId, setExpandedCreatorAppId] = useState<string | null>(null);

  // Creator Management State
  const [, setEditingCreator] = useState<Creator | null>(null);

  // Session Management State
  const [managingSessionsFor] = useState<Class | null>(null);

  // Dialog States
  const [, setShowUserSearchDialog] = useState(false);

  // Additional missing state variables
  const [, setEditingClass] = useState<Class | null>(null);
  const [denyingAdminRefundId, setDenyingAdminRefundId] = useState<string | null>(null);
  const [denyAdminReason, setDenyAdminReason] = useState('');
  const [cancelOrderId, setCancelOrderId] = useState('');
  const [cancelTicketReason, setCancelTicketReason] = useState('');
  const [, setEditingSession] = useState<ClassSession | null>(null);
  const [, setSessionForm] = useState<Record<string, unknown>>({});
  const [, setSelectedUserToPromote] = useState<Record<string, unknown> | null>(null);
  const [, setUserSearchQuery] = useState('');

  // Class Management State removed - not used in current implementation

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      redirectToLogin(500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  // Data queries removed - not used in current admin implementation

  const { data: partnerApplications = [] } = useQuery<PartnerApplication[]>({
    queryKey: ['/api/admin/partner-applications'],
    enabled: !!user && user.role === 'admin',
  });

  // Creator applications (new approval flow)
  interface CreatorApplicationRow {
    id: string;
    userId: string;
    displayName: string;
    phoneNumber?: string | null;
    bio?: string | null;
    creatorHeadline?: string | null;
    portfolioUrl?: string | null;
    experience?: string | null;
    status: string;
    createdAt: string;
    adminNotes?: string | null;
    proposedContent?: string | null;
    experienceLevel?: string | null;
    hasExistingAudience?: boolean | null;
    audienceDescription?: string | null;
    primaryLocation?: string | null;
    referralSource?: string | null;
    applicant?: {
      id: string;
      email: string;
      phoneNumber?: string | null;
      firstName: string;
      lastName: string;
      isActive: boolean;
    };
  }
  const { data: creatorApplications = [] } = useQuery<CreatorApplicationRow[]>({
    queryKey: ['/api/admin/creator-applications'],
    enabled: !!user && user.role === 'admin',
  });
  const getCreatorApplicationPhone = (application: CreatorApplicationRow) =>
    application.phoneNumber || application.applicant?.phoneNumber || null;

  const reviewCreatorApplicationMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      adminNotes,
    }: {
      id: string;
      status: string;
      adminNotes?: string;
    }) => {
      return await apiRequest('PUT', `/api/admin/creator-applications/${id}/status`, {
        status,
        adminNotes,
      });
    },
    onSuccess: () => {
      toast({ title: 'Application updated' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creator-applications'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Could not update application.',
        variant: 'destructive',
      });
    },
  });

  const { data: pageContentData } = useQuery<AdminPageContentResponseDto>({
    queryKey: [`/api/admin/content/${selectedPage}`],
    enabled: !!user && user.role === 'admin',
  });

  // Creators list for admin management
  const { data: adminCreatorsPage } = useQuery<PaginatedResponse<AdminCreatorRow>>({
    queryKey: ['/api/admin/creators'],
    enabled: !!user && user.role === 'admin',
  });
  const adminCreators = adminCreatorsPage?.data ?? [];

  const { data: emailHealth } = useQuery<AdminEmailHealthSummary>({
    queryKey: ['/api/admin/email/health'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: deadLetters = [] } = useQuery<DeadLetteredEmailRow[]>({
    queryKey: ['/api/admin/email/dead-letters'],
    enabled: !!user && user.role === 'admin',
  });

  const replayDeadLetterMutation = useMutation({
    mutationFn: async (emailId: string) => {
      return await apiRequest('POST', `/api/admin/email/dead-letters/${emailId}/replay`);
    },
    onSuccess: () => {
      toast({ title: 'Email replay queued' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/dead-letters'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/health'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Replay failed',
        description: error.message || 'Could not replay that dead-lettered email.',
        variant: 'destructive',
      });
    },
  });

  const dismissDeadLetterMutation = useMutation({
    mutationFn: async (target: { ids: string[] } | { all: true }) => {
      const res = await apiRequest('POST', '/api/admin/email/dead-letters/dismiss', target);
      return (await res.json()) as { dismissed: number };
    },
    onSuccess: ({ dismissed }) => {
      toast({ title: `Dismissed ${dismissed} failed email${dismissed === 1 ? '' : 's'}` });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/dead-letters'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/health'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Dismiss failed',
        description: error.message || 'Could not dismiss those emails.',
        variant: 'destructive',
      });
    },
  });

  const { data: supportCheckouts = [], isLoading: supportCheckoutsLoading } = useQuery<
    SupportCheckoutRow[]
  >({
    queryKey: ['/api/admin/support/checkouts', supportCheckoutStatus, supportCheckoutSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (supportCheckoutStatus) params.set('status', supportCheckoutStatus);
      if (supportCheckoutSearch.trim()) params.set('search', supportCheckoutSearch.trim());
      const response = await apiRequest('GET', `/api/admin/support/checkouts?${params}`);
      return await response.json();
    },
    enabled: !!user && user.role === 'admin',
  });

  const expireCheckoutMutation = useMutation({
    mutationFn: async (checkoutSessionId: string) => {
      const response = await apiRequest(
        'POST',
        `/api/admin/support/checkouts/${checkoutSessionId}/expire`
      );
      return await response.json();
    },
    onSuccess: result => {
      toast({
        title: result.expired ? 'Checkout expired' : 'No expiry needed',
        description: result.alreadyFinalized
          ? 'That checkout was already completed.'
          : 'Seat holds were released where applicable.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/support/checkouts'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Expire failed',
        description: error.message || 'Could not expire that checkout session.',
        variant: 'destructive',
      });
    },
  });

  const { data: pendingBlogPosts = [] } = useQuery<{ id: string }[]>({
    queryKey: ['/api/admin/blog', 'pending_review'],
    queryFn: () => authedJson<{ id: string }[]>('/api/admin/blog?status=pending_review'),
    refetchInterval: 60_000,
  });

  const { data: adminRefundRequests = [], isLoading: adminRefundsLoading } = useQuery<any[]>({
    queryKey: ['/api/refunds/requests/admin'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/refunds/requests/admin');
      return await response.json();
    },
  });

  const adminReviewRefundMutation = useMutation({
    mutationFn: async ({
      refundRequestId,
      action,
      denialReason,
      feeBearer,
    }: {
      refundRequestId: string;
      action: 'approve' | 'deny';
      denialReason?: string;
      feeBearer?: 'customer' | 'creator' | 'platform';
    }) => {
      const response = await apiRequest('POST', `/api/refunds/requests/${refundRequestId}/review`, {
        action,
        denialReason,
        feeBearer,
      });
      return await response.json();
    },
    onSuccess: (_data, { action }) => {
      toast({
        title: action === 'approve' ? 'Refund Approved' : 'Refund Denied',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/refunds/requests/admin'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Review failed',
        description: error.message || 'Could not process refund review.',
        variant: 'destructive',
      });
    },
  });

  const adminCancelTicketMutation = useMutation({
    mutationFn: async ({
      orderId,
      feeBearer,
      reason,
    }: {
      orderId: string;
      feeBearer: 'customer' | 'creator' | 'platform';
      reason?: string;
    }) => {
      const response = await apiRequest('POST', `/api/refunds/orders/${orderId}/cancel`, {
        feeBearer,
        reason,
      });
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Ticket canceled',
        description: 'The student has been refunded.',
      });
      setCancelOrderId('');
      setCancelTicketReason('');
      queryClient.invalidateQueries({ queryKey: ['/api/refunds/requests/admin'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/support/checkouts'] });
      // Any cached roster/enrollment views are now stale (staleTime is Infinity).
      queryClient.invalidateQueries({
        predicate: query => {
          const key = String(query.queryKey[0] ?? '');
          return key.includes('/enrollments') || key.includes('/attendees');
        },
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not cancel ticket',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateCreatorMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Creator> }) => {
      return await apiRequest('PUT', `/api/admin/creators/${id}`, data);
    },
    onSuccess: async () => {
      toast({ title: 'Creator updated' });
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/creators/featured'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Could not update creator.',
        variant: 'destructive',
      });
    },
  });

  // Create new category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await apiRequest('POST', '/api/categories', data);
      return (await res.json()) as { id: string; name: string; description?: string };
    },
    onSuccess: (newCategory: { id: string; name: string; description?: string }) => {
      toast({
        title: 'Category Created',
        description: `New category "${newCategory.name}" has been created successfully.`,
      });
      setNewCategory({ name: '', description: '' });
      // Refresh categories list
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
    },
    onError: (error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error));
      if (isUnauthorizedError(err)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Category Creation Failed',
        description: err.message || 'Failed to create category. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: {
      email: string;
      firstName: string;
      lastName: string;
      role: string;
    }) => {
      return await apiRequest('POST', '/api/admin/users', data);
    },
    onSuccess: () => {
      toast({
        title: 'User Created',
        description: 'New user has been successfully created.',
      });
      // User creation completed successfully
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/stats'] });
    },
    onError: _error => {
      toast({
        title: 'Creation Failed',
        description: 'Failed to create user. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user role mutation
  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      return await apiRequest('PUT', `/api/admin/users/${userId}/role`, { role });
    },
    onSuccess: () => {
      toast({
        title: 'Role Updated',
        description: 'User role has been successfully updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update user role. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user status mutation
  const updateUserStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      return await apiRequest('PUT', `/api/admin/users/${userId}/status`, { isActive });
    },
    onSuccess: () => {
      toast({
        title: 'Status Updated',
        description: 'User status has been successfully updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update user status. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user profile mutation removed - not used in current implementation

  // User deletion mutation removed (unused in current implementation)

  const approveApplicationMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      return await apiRequest('PUT', `/api/admin/partner-applications/${applicationId}/status`, {
        status: 'approved',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Partner Application Approved',
        description:
          'This marks the partnership inquiry as approved. Creator access still requires creator application approval.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/partner-applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Approval Failed',
        description: 'Failed to approve application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      return await apiRequest('DELETE', `/api/admin/partner-applications/${applicationId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Application Deleted',
        description: 'Partner application has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/partner-applications'] });
      setIsViewingApplication(false);
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateContentMutation = useMutation({
    mutationFn: async (data: {
      pageSlug: string;
      sectionKey: string;
      contentValue: string;
      contentType: string;
    }) => {
      return await apiRequest('PUT', `/api/admin/content/${data.pageSlug}/${data.sectionKey}`, {
        content: data.contentValue,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Content Updated',
        description: 'Page content has been successfully updated.',
      });
      setEditingContent(null);
      queryClient.invalidateQueries({ queryKey: [`/api/admin/content/${selectedPage}`] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update content. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Creator update mutation removed (unused in current implementation)

  // Creator create mutation removed (unused in current implementation)

  // Creator delete mutation removed (unused in current implementation)

  // Class Management Mutations
  // Class create mutation removed (unused in current implementation)

  // Class update mutation removed (unused in current implementation)

  // Class delete mutation removed (unused in current implementation)

  // Class archive mutation removed (unused in current implementation)

  // Class unarchive mutation removed (unused in current implementation)

  // Session Management Mutations
  // Sessions query removed (unused in current implementation)

  // Session create mutation removed (unused in current implementation)

  // Session update mutation removed (unused in current implementation)

  // Session delete mutation removed (unused in current implementation)

  // Promote to creator mutation removed (unused in current implementation)

  if (authLoading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        <div
          className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!isAuthenticated || !user || user.role !== 'admin') {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h1 className="text-2xl font-semibold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access this admin dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Handlers removed (unused in current implementation)

  return (
    <PublicShell routeKey="admin-dashboard">
      <div className="page-shell py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage content, applications, and promo codes</p>
        </div>

        {!!emailHealth?.activeAlerts?.length && (
          <Card className="mb-6 border-amber-300/60 bg-amber-50/80">
            <CardHeader>
              <CardTitle className="text-amber-950">Operational Alerts</CardTitle>
              <CardDescription className="text-amber-900/80">
                Email delivery and usage thresholds need attention.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {emailHealth.activeAlerts.map(alert => (
                <div
                  key={alert.code}
                  className="rounded-lg border border-amber-300/60 bg-white/70 p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-amber-950">{alert.title}</p>
                    <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                      {alert.severity}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm text-amber-900/80">{alert.message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Mobile section picker */}
        {(() => {
          const pendingCreatorApps = creatorApplications.filter(a => a.status === 'pending').length;
          const navGroups = buildNavGroups(pendingBlogPosts.length, pendingCreatorApps);
          const allItems = navGroups.flatMap(g => g.items);
          const activeLabel = allItems.find(i => i.value === activeSection)?.label ?? 'Section';
          return (
            <>
              <div className="lg:hidden mb-4">
                <Select value={activeSection} onValueChange={setActiveSection}>
                  <SelectTrigger className="w-full">
                    <SelectValue>{activeLabel}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {navGroups.map(group => (
                      <React.Fragment key={group.label}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {group.label}
                        </div>
                        {group.items.map(item => (
                          <SelectItem key={item.value} value={item.value}>
                            <span className="flex items-center gap-2">
                              {item.icon}
                              {item.label}
                              {!!item.badge && (
                                <span className="ml-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[18px] text-center">
                                  {item.badge}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-8 items-start">
                {/* Desktop sidebar */}
                <nav className="hidden lg:block w-48 shrink-0 sticky top-[calc(var(--shell-header-height)+1rem)]">
                  {navGroups.map(group => (
                    <div key={group.label} className="mb-5">
                      <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </p>
                      {group.items.map(item => (
                        <button
                          type="button"
                          key={item.value}
                          onClick={() => setActiveSection(item.value)}
                          className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                            activeSection === item.value
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          {item.icon}
                          <span className="flex-1 text-left">{item.label}</span>
                          {!!item.badge && (
                            <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[18px] text-center">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </nav>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <Tabs value={activeSection} onValueChange={setActiveSection}>
                    <TabsContent value="content" className="space-y-6">
                      <h2 className="text-2xl font-semibold">Content Management</h2>

                      <Card>
                        <CardHeader>
                          <CardTitle>Page Content</CardTitle>
                          <CardDescription>Manage page content sections</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="page-select">Select Page</Label>
                            <Select value={selectedPage} onValueChange={setSelectedPage}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a page" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="landing">Landing Page</SelectItem>
                                <SelectItem value="home">Home Page</SelectItem>
                                <SelectItem value="navigation">Navigation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            {(pageContentData?.sections ?? []).map(content => (
                              <div
                                key={content.id}
                                className="flex items-center justify-between p-4 border rounded-lg"
                              >
                                <div>
                                  <h3 className="font-medium">{content.sectionKey}</h3>
                                  <p className="text-sm text-muted-foreground truncate max-w-md">
                                    {content.contentValue}
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingContent(content);
                                    setContentForm({
                                      sectionKey: content.sectionKey,
                                      contentValue: content.contentValue,
                                      contentType: content.contentType,
                                    });
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {editingContent && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Edit Content: {editingContent.sectionKey}</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label htmlFor="content-type">Content Type</Label>
                              <Select
                                value={contentForm.contentType}
                                onValueChange={value =>
                                  setContentForm({ ...contentForm, contentType: value })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="html">HTML</SelectItem>
                                  <SelectItem value="json">JSON</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor="content-value">Content Value</Label>
                              <Textarea
                                id="content-value"
                                value={contentForm.contentValue}
                                onChange={e =>
                                  setContentForm({ ...contentForm, contentValue: e.target.value })
                                }
                                rows={6}
                              />
                            </div>

                            <div className="flex gap-2">
                              <Button
                                onClick={() => {
                                  updateContentMutation.mutate({
                                    pageSlug: selectedPage,
                                    sectionKey: editingContent.sectionKey,
                                    contentValue: contentForm.contentValue,
                                    contentType: contentForm.contentType,
                                  });
                                }}
                                disabled={updateContentMutation.isPending}
                              >
                                {updateContentMutation.isPending ? 'Saving...' : 'Save Changes'}
                              </Button>
                              <Button variant="outline" onClick={() => setEditingContent(null)}>
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    <TabsContent value="applications" className="space-y-6">
                      <h2 className="text-2xl font-semibold">Partner Applications</h2>

                      <Card>
                        <CardHeader>
                          <CardTitle>All Applications</CardTitle>
                          <CardDescription>
                            Review partnership inquiries. Approving here does not grant creator
                            access.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {partnerApplications.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">
                              No applications found
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>Phone</TableHead>
                                  <TableHead>Specialty</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {partnerApplications.map((application: PartnerApplication) => (
                                  <TableRow key={application.id}>
                                    <TableCell className="font-medium">
                                      {application.name}
                                    </TableCell>
                                    <TableCell>{application.email}</TableCell>
                                    <TableCell>{application.phone || 'N/A'}</TableCell>
                                    <TableCell>{application.specialty}</TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          application.status === 'approved'
                                            ? 'default'
                                            : application.status === 'rejected'
                                              ? 'destructive'
                                              : 'secondary'
                                        }
                                      >
                                        {application.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {application.createdAt
                                        ? formatDateCompact(application.createdAt)
                                        : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedApplication(application);
                                            setIsViewingApplication(true);
                                          }}
                                        >
                                          <Eye className="w-4 h-4 mr-1" />
                                          View
                                        </Button>
                                        {application.status === 'pending' && (
                                          <Button
                                            size="sm"
                                            onClick={() =>
                                              approveApplicationMutation.mutate(application.id)
                                            }
                                            disabled={approveApplicationMutation.isPending}
                                          >
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                            {approveApplicationMutation.isPending
                                              ? 'Approving...'
                                              : 'Mark Approved'}
                                          </Button>
                                        )}
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() =>
                                            deleteApplicationMutation.mutate(application.id)
                                          }
                                          disabled={deleteApplicationMutation.isPending}
                                        >
                                          <Trash2 className="w-4 h-4 mr-1" />
                                          {deleteApplicationMutation.isPending
                                            ? 'Deleting...'
                                            : 'Delete'}
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>

                      {/* Application Details Modal */}
                      <Dialog open={isViewingApplication} onOpenChange={setIsViewingApplication}>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Application Details</DialogTitle>
                            <DialogDescription>
                              Review full application information for {selectedApplication?.name}
                            </DialogDescription>
                          </DialogHeader>
                          {selectedApplication && (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm font-medium">Name</Label>
                                  <p className="text-sm">{selectedApplication.name}</p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium">Email</Label>
                                  <p className="text-sm">{selectedApplication.email}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm font-medium">Phone</Label>
                                  <p className="text-sm">
                                    {selectedApplication.phone || 'Not provided'}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium">Application Date</Label>
                                  <p className="text-sm">
                                    {selectedApplication.createdAt
                                      ? formatDateCompact(selectedApplication.createdAt)
                                      : 'N/A'}
                                  </p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm font-medium">Specialty</Label>
                                  <p className="text-sm">{selectedApplication.specialty}</p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium">Status</Label>
                                  <Badge
                                    variant={
                                      selectedApplication.status === 'approved'
                                        ? 'default'
                                        : selectedApplication.status === 'rejected'
                                          ? 'destructive'
                                          : 'secondary'
                                    }
                                  >
                                    {selectedApplication.status}
                                  </Badge>
                                </div>
                              </div>
                              {selectedApplication.experience && (
                                <div>
                                  <Label className="text-sm font-medium">
                                    Experience & Qualifications
                                  </Label>
                                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">
                                    {selectedApplication.experience}
                                  </p>
                                </div>
                              )}
                              {selectedApplication.portfolio && (
                                <div>
                                  <Label className="text-sm font-medium">Portfolio/Website</Label>
                                  <a
                                    href={selectedApplication.portfolio}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline block mt-1"
                                  >
                                    {selectedApplication.portfolio}
                                  </a>
                                </div>
                              )}
                              {selectedApplication.message && (
                                <div>
                                  <Label className="text-sm font-medium">Additional Message</Label>
                                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">
                                    {selectedApplication.message}
                                  </p>
                                </div>
                              )}
                              <div>
                                <Label className="text-sm font-medium">Application Date</Label>
                                <p className="text-sm">
                                  {selectedApplication.createdAt
                                    ? formatDateCompact(selectedApplication.createdAt)
                                    : 'N/A'}
                                </p>
                              </div>
                            </div>
                          )}
                          <DialogFooter className="gap-2">
                            {selectedApplication?.status === 'pending' && (
                              <Button
                                onClick={() =>
                                  approveApplicationMutation.mutate(selectedApplication.id)
                                }
                                disabled={approveApplicationMutation.isPending}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {approveApplicationMutation.isPending
                                  ? 'Approving...'
                                  : 'Mark Partner Approved'}
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              onClick={() =>
                                selectedApplication &&
                                deleteApplicationMutation.mutate(selectedApplication.id)
                              }
                              disabled={deleteApplicationMutation.isPending}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {deleteApplicationMutation.isPending
                                ? 'Deleting...'
                                : 'Delete Application'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setIsViewingApplication(false)}
                            >
                              Close
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </TabsContent>

                    <TabsContent value="promo-codes" className="space-y-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-medium">Promo Codes</h3>
                          <p className="text-sm text-muted-foreground">
                            Manage discount codes for class purchases
                          </p>
                        </div>
                        <Dialog open={isAddingPromoCode} onOpenChange={setIsAddingPromoCode}>
                          <DialogTrigger asChild>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Promo Code
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Create Promo Code</DialogTitle>
                              <DialogDescription>
                                Create a new discount code for class purchases
                              </DialogDescription>
                            </DialogHeader>
                            <PromoCodeForm
                              onSuccess={() => {
                                setIsAddingPromoCode(false);
                              }}
                            />
                          </DialogContent>
                        </Dialog>
                      </div>

                      <PromoCodesList />
                    </TabsContent>

                    <TabsContent value="creator-apps" className="space-y-6">
                      <h2 className="text-2xl font-semibold">Creator Applications</h2>
                      <Card>
                        <CardHeader>
                          <CardTitle>Pending Applications</CardTitle>
                          <CardDescription>
                            Review and approve creator account requests
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {creatorApplications.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">
                              No applications found
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Applicant</TableHead>
                                  <TableHead>Display Name</TableHead>
                                  <TableHead>Specialty</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Submitted</TableHead>
                                  <TableHead>Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {creatorApplications.map(app => (
                                  <React.Fragment key={app.id}>
                                    <TableRow
                                      className="cursor-pointer"
                                      onClick={() =>
                                        setExpandedCreatorAppId(id =>
                                          id === app.id ? null : app.id
                                        )
                                      }
                                    >
                                      <TableCell className="font-medium">
                                        <div>
                                          <p>
                                            {[app.applicant?.firstName, app.applicant?.lastName]
                                              .filter(Boolean)
                                              .join(' ') || 'Unknown user'}
                                          </p>
                                          <p className="text-xs font-normal text-muted-foreground">
                                            {app.applicant?.email || app.userId}
                                          </p>
                                          {getCreatorApplicationPhone(app) && (
                                            <p className="text-xs font-normal text-muted-foreground">
                                              {displayPhoneNumber(getCreatorApplicationPhone(app)!)}
                                            </p>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>{app.displayName}</TableCell>
                                      <TableCell>{app.creatorHeadline || '—'}</TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={
                                            app.status === 'approved'
                                              ? 'default'
                                              : app.status === 'rejected'
                                                ? 'destructive'
                                                : 'secondary'
                                          }
                                        >
                                          {app.status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>{formatDateCompact(app.createdAt)}</TableCell>
                                      <TableCell>
                                        {app.status === 'pending' && (
                                          <div
                                            className="flex gap-2"
                                            onClick={e => e.stopPropagation()}
                                          >
                                            <Button
                                              size="sm"
                                              variant="default"
                                              onClick={() =>
                                                reviewCreatorApplicationMutation.mutate({
                                                  id: app.id,
                                                  status: 'approved',
                                                })
                                              }
                                              disabled={reviewCreatorApplicationMutation.isPending}
                                            >
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                              Approve
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="destructive"
                                              onClick={() =>
                                                reviewCreatorApplicationMutation.mutate({
                                                  id: app.id,
                                                  status: 'rejected',
                                                })
                                              }
                                              disabled={reviewCreatorApplicationMutation.isPending}
                                            >
                                              Reject
                                            </Button>
                                          </div>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    {expandedCreatorAppId === app.id && (
                                      <TableRow>
                                        <TableCell colSpan={6} className="bg-muted/30 p-4">
                                          <div className="grid grid-cols-2 gap-4 text-sm">
                                            {app.bio && (
                                              <div className="col-span-2">
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Bio
                                                </p>
                                                <p>{app.bio}</p>
                                              </div>
                                            )}
                                            {app.proposedContent && (
                                              <div className="col-span-2">
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  What they'd teach
                                                </p>
                                                <p>{app.proposedContent}</p>
                                              </div>
                                            )}
                                            {app.experienceLevel && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Experience level
                                                </p>
                                                <p className="capitalize">
                                                  {app.experienceLevel.replace(/_/g, ' ')}
                                                </p>
                                              </div>
                                            )}
                                            {app.primaryLocation && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Primary location
                                                </p>
                                                <p>{app.primaryLocation}</p>
                                              </div>
                                            )}
                                            {getCreatorApplicationPhone(app) && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Phone
                                                </p>
                                                <p>
                                                  {displayPhoneNumber(
                                                    getCreatorApplicationPhone(app)!
                                                  )}
                                                </p>
                                              </div>
                                            )}
                                            {app.hasExistingAudience != null && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Existing audience
                                                </p>
                                                <p>{app.hasExistingAudience ? 'Yes' : 'No'}</p>
                                              </div>
                                            )}
                                            {app.audienceDescription && (
                                              <div className="col-span-2">
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Audience description
                                                </p>
                                                <p>{app.audienceDescription}</p>
                                              </div>
                                            )}
                                            {app.experience && (
                                              <div className="col-span-2">
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Experience
                                                </p>
                                                <p>{app.experience}</p>
                                              </div>
                                            )}
                                            {app.portfolioUrl && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Portfolio
                                                </p>
                                                <a
                                                  href={app.portfolioUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-primary hover:underline"
                                                >
                                                  {app.portfolioUrl}
                                                </a>
                                              </div>
                                            )}
                                            {app.referralSource && (
                                              <div>
                                                <p className="font-medium text-muted-foreground mb-1">
                                                  Referral
                                                </p>
                                                <p className="capitalize">
                                                  {app.referralSource.replace(/_/g, ' ')}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="creators" className="space-y-6">
                      <CreatorsTab
                        creators={adminCreators}
                        onToggleFeatured={(id, next) =>
                          updateCreatorMutation.mutate({ id, data: { isFeatured: next } })
                        }
                        onToggleActive={(id, next) =>
                          updateCreatorMutation.mutate({ id, data: { isActive: next } })
                        }
                        onTogglePartner={(id, next) =>
                          updateCreatorMutation.mutate({ id, data: { isPartner: next } })
                        }
                      />
                    </TabsContent>

                    <TabsContent value="email-ops" className="space-y-6">
                      <EmailOperationsTab
                        emailHealth={emailHealth}
                        deadLetters={deadLetters}
                        onReplay={emailId => replayDeadLetterMutation.mutate(emailId)}
                        isReplaying={replayDeadLetterMutation.isPending}
                        onDismiss={target => dismissDeadLetterMutation.mutate(target)}
                        isDismissing={dismissDeadLetterMutation.isPending}
                      />
                    </TabsContent>

                    <TabsContent value="refunds" className="space-y-6">
                      <Card>
                        <CardContent className="space-y-3 pt-4">
                          <div>
                            <h3 className="font-semibold">Cancel a ticket directly</h3>
                            <p className="text-sm text-muted-foreground">
                              Cancel and refund a single order without a student request. Find the
                              order ID in the Support (checkouts) tab or promo-code order history.
                            </p>
                          </div>
                          <Input
                            placeholder="Order ID"
                            value={cancelOrderId}
                            onChange={e => setCancelOrderId(e.target.value)}
                            className="font-mono text-sm"
                          />
                          <Input
                            placeholder="Reason (optional)"
                            value={cancelTicketReason}
                            onChange={e => setCancelTicketReason(e.target.value)}
                          />
                          <div className="space-y-1">
                            <p className="text-[11px] font-medium text-muted-foreground">
                              Refund &amp; cancel — who pays the card processing fee?
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                disabled={
                                  !cancelOrderId.trim() || adminCancelTicketMutation.isPending
                                }
                                onClick={() =>
                                  adminCancelTicketMutation.mutate({
                                    orderId: cancelOrderId.trim(),
                                    feeBearer: 'customer',
                                    reason: cancelTicketReason.trim() || undefined,
                                  })
                                }
                                title="Refund the amount minus the fee (customer pays the fee)"
                              >
                                Customer pays the fee
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={
                                  !cancelOrderId.trim() || adminCancelTicketMutation.isPending
                                }
                                onClick={() =>
                                  adminCancelTicketMutation.mutate({
                                    orderId: cancelOrderId.trim(),
                                    feeBearer: 'creator',
                                    reason: cancelTicketReason.trim() || undefined,
                                  })
                                }
                                title="Full refund; the creator pays the fee"
                              >
                                Creator pays the fee
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={
                                  !cancelOrderId.trim() || adminCancelTicketMutation.isPending
                                }
                                onClick={() =>
                                  adminCancelTicketMutation.mutate({
                                    orderId: cancelOrderId.trim(),
                                    feeBearer: 'platform',
                                    reason: cancelTicketReason.trim() || undefined,
                                  })
                                }
                                title="Full refund; Kalovate pays the fee"
                              >
                                Kalovate pays the fee
                              </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              For a free booking there&rsquo;s no fee — any choice simply cancels
                              it.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      <h2 className="text-2xl font-semibold">Refund Requests</h2>
                      {adminRefundsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                        </div>
                      ) : adminRefundRequests.length > 0 ? (
                        <div className="space-y-4">
                          {adminRefundRequests.map((req: any) => (
                            <Card key={req.id}>
                              <CardContent className="pt-4 space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="space-y-1">
                                    <p className="font-semibold">{req.classTitle}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {req.purchaserName} &middot; {req.purchaserEmail}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                      Creator: {req.creatorDisplayName}
                                    </p>
                                    {req.sessionStartDateTime && (
                                      <p className="text-sm text-muted-foreground">
                                        Session:{' '}
                                        {formatDateWithTimezone(req.sessionStartDateTime, {
                                          includeTime: true,
                                        })}
                                      </p>
                                    )}
                                    {req.createdAt && (
                                      <p className="text-xs text-muted-foreground">
                                        Requested{' '}
                                        {formatDateWithTimezone(req.createdAt, {
                                          includeTime: true,
                                        })}
                                      </p>
                                    )}
                                    {req.requestedReason && (
                                      <p className="text-sm italic text-muted-foreground">
                                        &ldquo;{req.requestedReason}&rdquo;
                                      </p>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      Policy: {req.cancellationPolicy}
                                    </p>
                                  </div>
                                  <div className="shrink-0 text-right space-y-1">
                                    {req.refundAmountCents != null && (
                                      <p className="font-semibold">
                                        ${(req.refundAmountCents / 100).toFixed(2)}
                                      </p>
                                    )}
                                    <Badge
                                      variant={
                                        req.status === 'pending'
                                          ? 'outline'
                                          : req.status === 'approved'
                                            ? 'default'
                                            : 'secondary'
                                      }
                                    >
                                      {req.status}
                                    </Badge>
                                  </div>
                                </div>
                                {(req.status === 'pending' || req.status === 'creator_review') &&
                                  denyingAdminRefundId !== req.id && (
                                    <div className="space-y-1 pt-1">
                                      {(req.orderNetAmountCents ?? 0) <= 0 ? (
                                        <>
                                          <p className="text-[11px] font-medium text-muted-foreground">
                                            Free booking — no payment or processing fee.
                                          </p>
                                          <div className="flex flex-wrap gap-2">
                                            <Button
                                              size="sm"
                                              onClick={() =>
                                                adminReviewRefundMutation.mutate({
                                                  refundRequestId: req.id,
                                                  action: 'approve',
                                                  feeBearer: 'creator',
                                                })
                                              }
                                              disabled={adminReviewRefundMutation.isPending}
                                            >
                                              Approve cancellation
                                            </Button>
                                            {req.denyAllowed && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  setDenyingAdminRefundId(req.id);
                                                  setDenyAdminReason('');
                                                }}
                                                disabled={adminReviewRefundMutation.isPending}
                                              >
                                                Deny
                                              </Button>
                                            )}
                                          </div>
                                          {!req.denyAllowed && (
                                            <p className="text-[11px] text-muted-foreground">
                                              Within the policy window — must be approved.
                                            </p>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <p className="text-[11px] font-medium text-muted-foreground">
                                            Approve — who pays the card processing fee?
                                          </p>
                                          <div className="flex flex-wrap gap-2">
                                            <Button
                                              size="sm"
                                              onClick={() =>
                                                adminReviewRefundMutation.mutate({
                                                  refundRequestId: req.id,
                                                  action: 'approve',
                                                  feeBearer: 'customer',
                                                })
                                              }
                                              disabled={adminReviewRefundMutation.isPending}
                                              title="Refund the amount shown (customer pays the fee)"
                                            >
                                              Customer pays the fee
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={() =>
                                                adminReviewRefundMutation.mutate({
                                                  refundRequestId: req.id,
                                                  action: 'approve',
                                                  feeBearer: 'creator',
                                                })
                                              }
                                              disabled={adminReviewRefundMutation.isPending}
                                              title="Full refund; the creator pays the fee"
                                            >
                                              Creator pays the fee
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={() =>
                                                adminReviewRefundMutation.mutate({
                                                  refundRequestId: req.id,
                                                  action: 'approve',
                                                  feeBearer: 'platform',
                                                })
                                              }
                                              disabled={adminReviewRefundMutation.isPending}
                                              title="Full refund; Kalovate pays the fee"
                                            >
                                              Kalovate pays the fee
                                            </Button>
                                            {req.denyAllowed && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  setDenyingAdminRefundId(req.id);
                                                  setDenyAdminReason('');
                                                }}
                                                disabled={adminReviewRefundMutation.isPending}
                                              >
                                                Deny
                                              </Button>
                                            )}
                                          </div>
                                          <p className="text-[11px] text-muted-foreground">
                                            “Customer pays the fee” refunds the amount minus the
                                            fee; “Creator” and “Kalovate” are full refunds with that
                                            party paying the fee.
                                            {!req.denyAllowed &&
                                              ' Within the policy window — must be refunded.'}
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  )}
                                {(req.status === 'pending' || req.status === 'creator_review') &&
                                  denyingAdminRefundId === req.id && (
                                    <div className="space-y-2 pt-1">
                                      <Input
                                        placeholder="Denial reason (required)"
                                        value={denyAdminReason}
                                        onChange={e => setDenyAdminReason(e.target.value)}
                                        className="rounded-xl"
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => {
                                            if (!denyAdminReason.trim()) return;
                                            adminReviewRefundMutation.mutate({
                                              refundRequestId: req.id,
                                              action: 'deny',
                                              denialReason: denyAdminReason,
                                            });
                                            setDenyingAdminRefundId(null);
                                          }}
                                          disabled={
                                            !denyAdminReason.trim() ||
                                            adminReviewRefundMutation.isPending
                                          }
                                        >
                                          Confirm denial
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setDenyingAdminRefundId(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                {req.status === 'denied' && req.denialReason && (
                                  <p className="text-sm text-muted-foreground">
                                    Denial reason: {req.denialReason}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-8 text-muted-foreground">
                          No refund requests.
                        </p>
                      )}
                    </TabsContent>

                    <TabsContent value="support" className="space-y-6">
                      <CheckoutSupportTab
                        checkouts={supportCheckouts}
                        isLoading={supportCheckoutsLoading}
                        search={supportCheckoutSearch}
                        status={supportCheckoutStatus}
                        onSearchChange={setSupportCheckoutSearch}
                        onStatusChange={setSupportCheckoutStatus}
                        onExpire={checkoutSessionId =>
                          expireCheckoutMutation.mutate(checkoutSessionId)
                        }
                        isExpiring={expireCheckoutMutation.isPending}
                      />
                    </TabsContent>

                    <TabsContent value="moderation" className="space-y-6">
                      <ModerationTab />
                    </TabsContent>
                    <TabsContent value="locations" className="space-y-6">
                      <LocationsTab />
                    </TabsContent>
                    <TabsContent value="payments" className="space-y-8">
                      <FinancialsOverview />
                      <PayoutsAdmin />
                      <ConnectedAccountsAdmin />
                      <LedgerAdmin />
                      <AmbassadorsAdmin />
                    </TabsContent>
                    <TabsContent value="fee-profiles" className="space-y-6">
                      <FeeProfilesTab />
                    </TabsContent>
                    <TabsContent value="punch-passes" className="space-y-6">
                      <PassProductsAdmin />
                      <Card>
                        <CardHeader>
                          <CardTitle>Pass holders</CardTitle>
                          <CardDescription>
                            Restore or remove punches, extend expiry, or return a booking&apos;s
                            punch for customer service. Every change is logged with a reason.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <PassHoldersManager />
                        </CardContent>
                      </Card>
                    </TabsContent>
                    <TabsContent value="calendar" className="space-y-6">
                      <CalendarAdminTab />
                    </TabsContent>
                    <TabsContent value="blog" className="space-y-6">
                      <BlogAdminTab />
                    </TabsContent>

                    <TabsContent value="kill-switches" className="space-y-6">
                      <KillSwitchesTab />
                    </TabsContent>
                    <TabsContent value="audit" className="space-y-6">
                      <AuditLogAdmin />
                    </TabsContent>
                    <TabsContent value="user-mgmt" className="space-y-6">
                      <AdminClassManagement />
                    </TabsContent>
                    <TabsContent value="home" className="space-y-6">
                      <AdminHome onNavigate={setActiveSection} />
                    </TabsContent>
                    <TabsContent value="orders" className="space-y-6">
                      <OrdersLookup />
                    </TabsContent>
                  </Tabs>
                </div>
                {/* flex-1 content */}
              </div>
              {/* flex gap-8 */}
            </>
          );
        })()}
      </div>
    </PublicShell>
  );
}

interface CommunityReportRow {
  id: string;
  reason: string;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  topicId: string | null;
  replyId: string | null;
  reporterFirstName: string | null;
  reporterLastName: string | null;
  reporterEmail: string | null;
  topicTitle: string | null;
  replyContent: string | null;
}

function ModerationTab() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('pending');

  const {
    data: reports = [],
    isLoading,
    refetch,
  } = useQuery<CommunityReportRow[]>({
    queryKey: ['/api/admin/community/reports', statusFilter],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/community/reports?status=${statusFilter}`);
      return res.json();
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      adminNotes,
    }: {
      id: string;
      status: string;
      adminNotes?: string;
    }) => {
      await apiRequest('PUT', `/api/admin/community/reports/${id}`, { status, adminNotes });
    },
    onSuccess: () => {
      toast({ title: 'Report actioned' });
      refetch();
    },
    onError: () => toast({ title: 'Failed to action report', variant: 'destructive' }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Community Moderation</h2>
        <div className="flex gap-2">
          {(['pending', 'actioned', 'dismissed'] as const).map(s => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : reports.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No {statusFilter} reports.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reporter</TableHead>
              <TableHead>Content</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Date</TableHead>
              {statusFilter === 'pending' && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map(report => (
              <TableRow key={report.id}>
                <TableCell>
                  <div className="text-sm">
                    {[report.reporterFirstName, report.reporterLastName]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">{report.reporterEmail}</div>
                </TableCell>
                <TableCell className="max-w-64">
                  {report.topicTitle && (
                    <div className="text-sm font-medium truncate">Topic: {report.topicTitle}</div>
                  )}
                  {report.replyContent && (
                    <div className="text-sm text-muted-foreground truncate">
                      Reply: {report.replyContent}
                    </div>
                  )}
                </TableCell>
                <TableCell className="max-w-48">
                  <p className="text-sm truncate">{report.reason}</p>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatDateCompact(report.createdAt)}
                </TableCell>
                {statusFilter === 'pending' && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          actionMutation.mutate({ id: report.id, status: 'dismissed' })
                        }
                        disabled={actionMutation.isPending}
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => actionMutation.mutate({ id: report.id, status: 'actioned' })}
                        disabled={actionMutation.isPending}
                      >
                        Action
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function EmailOperationsTab({
  emailHealth,
  deadLetters,
  onReplay,
  isReplaying,
  onDismiss,
  isDismissing,
}: {
  emailHealth?: AdminEmailHealthSummary;
  deadLetters: DeadLetteredEmailRow[];
  onReplay: (emailId: string) => void;
  isReplaying: boolean;
  onDismiss: (target: { ids: string[] } | { all: true }) => void;
  isDismissing: boolean;
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Email Operations</h2>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Queue Health</CardTitle>
            <CardDescription>Dead-letter queue depth and active email alerts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  Dead-lettered in last {emailHealth?.deadLetterWindowHours ?? 24}h
                </p>
                <p className="text-2xl font-semibold">{emailHealth?.deadLetterRecentCount ?? 0}</p>
                <p className="text-xs text-muted-foreground">
                  {emailHealth?.deadLetterCount ?? 0} all time
                </p>
              </div>
              {/* Badge tracks the windowed count so the dashboard and the alert
                  email never disagree — an all-time count only grows and would
                  sit red forever. */}
              <Badge
                variant={
                  (emailHealth?.deadLetterRecentCount ?? 0) >
                  (emailHealth?.deadLetterThreshold ?? 10)
                    ? 'destructive'
                    : 'secondary'
                }
              >
                Threshold {emailHealth?.deadLetterThreshold ?? 10}
              </Badge>
            </div>

            {emailHealth?.activeAlerts?.length ? (
              <div className="space-y-3">
                {emailHealth.activeAlerts.map(alert => (
                  <div
                    key={alert.code}
                    className="rounded-lg border border-border/60 bg-muted/30 p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium">{alert.title}</p>
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{alert.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active email health alerts.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Usage By Provider</CardTitle>
            <CardDescription>Rolling 30-day and daily external recipient counts</CardDescription>
          </CardHeader>
          <CardContent>
            {emailHealth?.usageByProvider?.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Rolling 30 Days</TableHead>
                    <TableHead>Today</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailHealth.usageByProvider.map(usage => (
                    <TableRow key={usage.provider}>
                      <TableCell className="font-medium">{usage.provider}</TableCell>
                      <TableCell>{usage.rolling30ExternalRecipientCount}</TableCell>
                      <TableCell>{usage.dailyExternalRecipientCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">No telemetry recorded yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle>Dead-Letter Queue</CardTitle>
              <CardDescription>
                Replay failed transactional emails after the underlying issue is fixed, or dismiss
                ones that no longer need action (they stay on record)
              </CardDescription>
            </div>
            {deadLetters.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                disabled={isDismissing}
                onClick={() => {
                  if (
                    window.confirm(
                      `Dismiss all ${deadLetters.length} failed emails? They stay on record and can still be replayed.`
                    )
                  ) {
                    onDismiss({ all: true });
                  }
                }}
              >
                Dismiss all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {deadLetters.length === 0 ? (
            <p className="text-sm text-muted-foreground">No dead-lettered emails.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Last Error</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deadLetters.map(email => (
                  <TableRow key={email.id}>
                    <TableCell className="font-medium">{email.recipientEmail}</TableCell>
                    <TableCell>{email.templateKey}</TableCell>
                    <TableCell>{email.sendAttempts}</TableCell>
                    <TableCell className="max-w-sm truncate">{email.lastError || '—'}</TableCell>
                    <TableCell>{formatDateCompact(email.updatedAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onReplay(email.id)}
                          disabled={isReplaying}
                        >
                          Replay
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDismiss({ ids: [email.id] })}
                          disabled={isDismissing}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CheckoutSupportTab({
  checkouts,
  isLoading,
  search,
  status,
  onSearchChange,
  onStatusChange,
  onExpire,
  isExpiring,
}: {
  checkouts: SupportCheckoutRow[];
  isLoading: boolean;
  search: string;
  status: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onExpire: (checkoutSessionId: string) => void;
  isExpiring: boolean;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Checkout Support</h2>
        <p className="text-sm text-muted-foreground">
          Inspect checkout sessions, order state, and held seats before running a repair action.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Checkout Sessions</CardTitle>
          <CardDescription>
            Force-expire only active checkouts that are stuck or no longer recoverable.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={event => onSearchChange(event.target.value)}
                placeholder="Search checkout id, student email, or class"
                className="pl-9"
              />
            </div>
            <Select value={status} onValueChange={onStatusChange}>
              <SelectTrigger className="w-full md:w-44">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="abandoned">Abandoned</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : checkouts.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No checkout sessions match the current filters.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Checkout</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Class / Session</TableHead>
                  <TableHead>Seats</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checkouts.map(checkout => {
                  const sessionCapacity =
                    checkout.sessionMaxStudents == null ? 'unlimited' : checkout.sessionMaxStudents;
                  return (
                    <TableRow key={checkout.id}>
                      <TableCell>
                        <div className="font-mono text-xs">{checkout.id}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <Badge variant={checkout.status === 'active' ? 'default' : 'secondary'}>
                            {checkout.status}
                          </Badge>
                          {checkout.promoCode ? (
                            <Badge variant="outline">{checkout.promoCode}</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {[checkout.userFirstName, checkout.userLastName]
                            .filter(Boolean)
                            .join(' ') || 'Student'}
                        </div>
                        <div className="text-xs text-muted-foreground">{checkout.userEmail}</div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-64 truncate text-sm font-medium">
                          {checkout.classTitle}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateCompact(checkout.sessionStartDateTime)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          Checkout: {checkout.quantity} / held {checkout.seatHoldCount}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Session: {checkout.enrollmentCount} enrolled,{' '}
                          {checkout.sessionSeatHoldCount} held, cap {sessionCapacity}
                        </div>
                      </TableCell>
                      <TableCell>
                        {checkout.orderId ? (
                          <>
                            <div className="font-mono text-xs">{checkout.orderId}</div>
                            <div className="text-xs text-muted-foreground">
                              {checkout.orderStatus} · $
                              {((checkout.orderNetAmountCents ?? 0) / 100).toFixed(2)}
                            </div>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">No order</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatDateCompact(checkout.expiresAt)}</div>
                        <div className="text-xs text-muted-foreground">
                          Updated {formatDateCompact(checkout.updatedAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onExpire(checkout.id)}
                          disabled={isExpiring || checkout.status !== 'active'}
                        >
                          Force expire
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Creators management tab
function CreatorsTab({
  creators,
  onToggleFeatured,
  onToggleActive,
  onTogglePartner,
}: {
  creators: AdminCreatorRow[];
  onToggleFeatured: (id: string, next: boolean) => void;
  onToggleActive: (id: string, next: boolean) => void;
  onTogglePartner: (id: string, next: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold">Creators</h2>
      <Card>
        <CardHeader>
          <CardTitle>Manage Creators</CardTitle>
          <CardDescription>
            Active controls public discovery. Partner is a trust badge only. Featured promotes on
            the homepage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Partner badge</TableHead>
                <TableHead>Featured</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creators.map(c => {
                const creatorId = c.id;
                return (
                  <TableRow key={c.userId}>
                    <TableCell>
                      {c.displayName ||
                        `${c.userFirstName || ''} ${c.userLastName || ''}`.trim() ||
                        c.userEmail}
                    </TableCell>
                    <TableCell>{c.userEmail}</TableCell>
                    <TableCell>
                      <Badge variant={c.isVerified ? 'default' : 'secondary'}>
                        {c.isVerified ? 'Verified' : 'Unverified'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {creatorId ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={c.isActive !== false}
                            onCheckedChange={next => onToggleActive(creatorId, next)}
                          />
                          <span className="text-sm">
                            {c.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {creatorId ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!c.isPartner}
                            onCheckedChange={next => onTogglePartner(creatorId, next)}
                          />
                          <span className="text-sm">{c.isPartner ? 'Partner' : 'None'}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {creatorId ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!c.isFeatured}
                            onCheckedChange={next => onToggleFeatured(creatorId, next)}
                          />
                          <span className="text-sm">
                            {c.isFeatured ? 'Featured' : 'Not featured'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No profile</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function PromoCodeForm({ onSuccess }: { onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: '',
    minimumAmount: '',
    classId: 'all',
    maxUses: '',
    expiresAt: '',
    firstUseOnly: false,
    appliesToPasses: false,
  });

  const { toast } = useToast();

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const code = Array.from(
      { length: 8 },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join('');
    setFormData(f => ({ ...f, code }));
  };

  const { data: classesPage } = useQuery<PaginatedResponse<Class>>({
    queryKey: ['/api/admin/classes', { limit: 100 }],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/classes?limit=100');
      return (await response.json()) as PaginatedResponse<Class>;
    },
  });
  const classes = classesPage?.data ?? [];

  const createPromoCodeMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const submitData = {
        ...data,
        classId: data.classId === 'all' ? null : data.classId,
        minimumAmount: data.minimumAmount || null,
        maxUses: data.maxUses || null,
        expiresAt: data.expiresAt || null,
      };
      const res = await apiRequest('POST', '/api/admin/promo-codes', submitData);
      return (await res.json()) as AdminPromoCodeDto;
    },
    onSuccess: newCode => {
      queryClient.setQueryData<AdminPromoCodeDto[]>(['/api/admin/promo-codes'], old =>
        old ? [newCode, ...old] : [newCode]
      );
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/promo-codes'] });
      toast({
        title: 'Promo Code Created',
        description: 'The promo code has been created successfully.',
      });
      onSuccess();
      setFormData({
        code: '',
        description: '',
        discountType: 'percentage',
        discountValue: '',
        minimumAmount: '',
        classId: 'all',
        maxUses: '',
        expiresAt: '',
        firstUseOnly: false,
        appliesToPasses: false,
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Creation Failed',
        description: 'Failed to create promo code. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code || !formData.discountValue) {
      toast({
        title: 'Validation Error',
        description: 'Code and discount value are required.',
        variant: 'destructive',
      });
      return;
    }
    createPromoCodeMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Promo Code *</Label>
          <div className="flex gap-2">
            <Input
              id="code"
              placeholder="SAVE20"
              value={formData.code}
              onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={generateCode}
              aria-label="Generate code"
            >
              <Shuffle className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="discountType">Discount Type</Label>
          <Select
            value={formData.discountType}
            onValueChange={(value: 'percentage' | 'fixed') =>
              setFormData({ ...formData, discountType: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage (%)</SelectItem>
              <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="classId">Apply To</Label>
        <Select
          value={formData.classId}
          onValueChange={value => setFormData({ ...formData, classId: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select class or all classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {Array.isArray(classes) &&
              classes.map((cls: Class) => (
                <SelectItem key={cls.id} value={cls.id}>
                  {cls.title}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="discountValue">
            Discount Value * {formData.discountType === 'percentage' ? '(%)' : '($)'}
          </Label>
          <Input
            id="discountValue"
            type="number"
            step={formData.discountType === 'percentage' ? '1' : '0.01'}
            min="0"
            max={formData.discountType === 'percentage' ? '100' : undefined}
            placeholder={formData.discountType === 'percentage' ? '20' : '5.00'}
            value={formData.discountValue}
            onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="minimumAmount">
            Minimum Amount ($){' '}
            <span className="text-muted-foreground">(optional - no minimum if empty)</span>
          </Label>
          <Input
            id="minimumAmount"
            type="number"
            step="0.01"
            min="0"
            placeholder="No minimum amount"
            value={formData.minimumAmount}
            onChange={e => setFormData({ ...formData, minimumAmount: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="maxUses">
            Max Uses <span className="text-muted-foreground">(optional - unlimited if empty)</span>
          </Label>
          <Input
            id="maxUses"
            type="number"
            min="1"
            placeholder="Unlimited uses"
            value={formData.maxUses}
            onChange={e => setFormData({ ...formData, maxUses: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <DateTimePicker
            id="expiresAt"
            label="Expires At"
            value={formData.expiresAt}
            onChange={value => setFormData({ ...formData, expiresAt: value })}
            placeholder="Optional - never expires if empty"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pc-description">Description</Label>
        <Textarea
          id="pc-description"
          placeholder="20% off all classes"
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          id="firstUseOnly"
          checked={formData.firstUseOnly}
          onCheckedChange={checked => setFormData({ ...formData, firstUseOnly: checked === true })}
        />
        <Label htmlFor="firstUseOnly" className="cursor-pointer">
          First use only (one per customer)
        </Label>
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          id="appliesToPasses"
          checked={formData.appliesToPasses}
          onCheckedChange={checked =>
            setFormData({ ...formData, appliesToPasses: checked === true })
          }
          data-testid="checkbox-applies-to-passes"
        />
        <Label htmlFor="appliesToPasses" className="cursor-pointer">
          Can be used on punch passes
        </Label>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={createPromoCodeMutation.isPending} className="flex-1">
          {createPromoCodeMutation.isPending ? 'Creating...' : 'Create Promo Code'}
        </Button>
      </div>
    </form>
  );
}

function PromoCodesList() {
  const [editingPromoCode, setEditingPromoCode] = useState<AdminPromoCodeDto | null>(null);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkExtendDate, setBulkExtendDate] = useState('');
  const { toast } = useToast();

  const {
    data: promoCodes,
    isLoading,
    isError,
    refetch,
  } = useQuery<AdminPromoCodeDto[]>({
    queryKey: ['/api/admin/promo-codes'],
  });

  const { data: expandedOrders } = useQuery<AdminPromoCodeOrderDto[]>({
    queryKey: ['/api/admin/promo-codes', expandedCode, 'orders'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/promo-codes/${expandedCode}/orders`);
      return res.json() as Promise<AdminPromoCodeOrderDto[]>;
    },
    enabled: !!expandedCode,
  });

  const promoCodesArray = promoCodes ?? [];
  const allSelected =
    promoCodesArray.length > 0 && promoCodesArray.every(p => selectedIds.has(p.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(promoCodesArray.map(p => p.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updatePromoCodeMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<AdminPromoCodeDto> & { id: string }) => {
      return await apiRequest('PUT', `/api/admin/promo-codes/${id}`, data);
    },
    onSuccess: () => {
      toast({ title: 'Promo Code Updated', description: 'Updated successfully.' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/promo-codes'] });
      setEditingPromoCode(null);
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update promo code.',
        variant: 'destructive',
      });
    },
  });

  const deletePromoCodeMutation = useMutation({
    mutationFn: async (id: string) => apiRequest('DELETE', `/api/admin/promo-codes/${id}`),
    onSuccess: (_data, id) => {
      toast({ title: 'Promo Code Deleted', description: 'Deleted successfully.' });
      queryClient.setQueryData<AdminPromoCodeDto[]>(['/api/admin/promo-codes'], old =>
        old ? old.filter(p => p.id !== id) : []
      );
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/promo-codes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Deletion Failed',
        description: 'Failed to delete promo code.',
        variant: 'destructive',
      });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (payload: { action: string; ids: string[]; expiresAt?: string }) =>
      apiRequest('POST', '/api/admin/promo-codes/bulk', payload),
    onSuccess: (_data, variables) => {
      toast({
        title: 'Bulk Action Applied',
        description: `${variables.ids.length} code(s) updated.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/promo-codes'] });
      setSelectedIds(new Set());
      setBulkExtendDate('');
    },
    onError: () => {
      toast({ title: 'Bulk Action Failed', variant: 'destructive' });
    },
  });

  const handleExportCsv = () => {
    window.location.href = '/api/admin/promo-codes/export.csv';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-border/70 bg-card p-8 text-center">
        <p className="text-sm text-destructive mb-3">Failed to load promo codes.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {selectedIds.size > 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button
              size="sm"
              variant="outline"
              disabled={bulkMutation.isPending}
              onClick={() => bulkMutation.mutate({ action: 'disable', ids: [...selectedIds] })}
            >
              Disable
            </Button>
            <div className="flex items-center gap-1">
              <Input
                type="date"
                className="h-8 w-36 text-sm"
                value={bulkExtendDate}
                onChange={e => setBulkExtendDate(e.target.value)}
                placeholder="Extend to…"
                aria-label="Extend expiry date"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!bulkExtendDate || bulkMutation.isPending}
                onClick={() =>
                  bulkMutation.mutate({
                    action: 'extend',
                    ids: [...selectedIds],
                    expiresAt: bulkExtendDate,
                  })
                }
              >
                Extend
              </Button>
            </div>
            <Button
              size="sm"
              variant="destructive"
              disabled={bulkMutation.isPending}
              onClick={() => {
                if (confirm(`Delete ${selectedIds.size} promo code(s)? This cannot be undone.`)) {
                  bulkMutation.mutate({ action: 'delete', ids: [...selectedIds] });
                }
              }}
            >
              Delete
            </Button>
          </div>
        ) : (
          <div />
        )}
        <Button size="sm" variant="outline" onClick={handleExportCsv} className="gap-1.5">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
            </TableHead>
            <TableHead className="w-6" />
            <TableHead>Code</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Uses / Limit</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {promoCodesArray.map(promoCode => (
            <React.Fragment key={promoCode.id}>
              <TableRow
                className="cursor-pointer hover:bg-muted/50"
                onClick={() =>
                  setExpandedCode(prev => (prev === promoCode.code ? null : promoCode.code))
                }
              >
                <TableCell onClick={e => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(promoCode.id)}
                    onCheckedChange={() => toggleOne(promoCode.id)}
                    aria-label={`Select ${promoCode.code}`}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {expandedCode === promoCode.code ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </TableCell>
                <TableCell className="font-medium font-mono">{promoCode.code}</TableCell>
                <TableCell>
                  {promoCode.discountType === 'percentage'
                    ? `${promoCode.discountValue}%`
                    : `$${promoCode.discountValue}`}
                  {promoCode.minimumAmount && (
                    <div className="text-xs text-muted-foreground">
                      Min: ${promoCode.minimumAmount}
                    </div>
                  )}
                  {promoCode.firstUseOnly && (
                    <div className="text-xs text-muted-foreground">First use only</div>
                  )}
                </TableCell>
                <TableCell>
                  {promoCode.usedCount}
                  {promoCode.maxUses ? `/${promoCode.maxUses}` : ''}
                </TableCell>
                <TableCell>
                  <Badge variant={promoCode.isActive ? 'default' : 'secondary'}>
                    {promoCode.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {promoCode.expiresAt ? formatDateCompact(promoCode.expiresAt) : 'Never'}
                </TableCell>
                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPromoCode(promoCode)}
                      aria-label="Edit promo code"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deletePromoCodeMutation.mutate(promoCode.id)}
                      disabled={deletePromoCodeMutation.isPending}
                      aria-label="Delete promo code"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>

              {expandedCode === promoCode.code && (
                <TableRow>
                  <TableCell colSpan={8} className="bg-muted/30 p-0">
                    <div className="px-6 py-4">
                      <p className="text-sm font-medium mb-3">Order history for {promoCode.code}</p>
                      {!expandedOrders ? (
                        <p className="text-sm text-muted-foreground">Loading…</p>
                      ) : expandedOrders.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No orders found for this code.
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Order ID</TableHead>
                              <TableHead>Student</TableHead>
                              <TableHead>Class</TableHead>
                              <TableHead>Discount</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {expandedOrders.map(order => (
                              <TableRow key={order.orderId}>
                                <TableCell className="font-mono text-xs">
                                  {order.orderId.slice(0, 8)}…
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">
                                    {order.studentFirstName || order.studentLastName
                                      ? `${order.studentFirstName ?? ''} ${order.studentLastName ?? ''}`.trim()
                                      : '—'}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {order.studentEmail ?? ''}
                                  </div>
                                </TableCell>
                                <TableCell>{order.classTitle ?? '—'}</TableCell>
                                <TableCell>-${(order.discountCents / 100).toFixed(2)}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant={order.status === 'paid' ? 'default' : 'secondary'}
                                  >
                                    {order.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>{formatDateCompact(order.createdAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {promoCodesArray.length === 0 && (
        <div className="text-center py-12">
          <Percent className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No promo codes yet</h3>
          <p className="text-muted-foreground">
            Create your first promo code to start offering discounts.
          </p>
        </div>
      )}

      {editingPromoCode && (
        <Dialog open={!!editingPromoCode} onOpenChange={() => setEditingPromoCode(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Promo Code</DialogTitle>
              <DialogDescription>Update the promo code details</DialogDescription>
            </DialogHeader>
            <EditPromoCodeForm
              promoCode={editingPromoCode}
              onSuccess={() => setEditingPromoCode(null)}
              onUpdate={data =>
                updatePromoCodeMutation.mutate({ id: editingPromoCode.id, ...data })
              }
              isLoading={updatePromoCodeMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditPromoCodeForm({
  promoCode,
  onSuccess,
  onUpdate,
  isLoading,
}: {
  promoCode: AdminPromoCodeDto;
  onSuccess: () => void;
  onUpdate: (data: Partial<AdminPromoCodeDto>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    code: promoCode.code,
    description: promoCode.description || '',
    discountType: promoCode.discountType as 'percentage' | 'fixed',
    discountValue: promoCode.discountValue,
    minimumAmount: promoCode.minimumAmount || '',
    maxUses: promoCode.maxUses?.toString() || '',
    expiresAt: promoCode.expiresAt ? utcToLocalDatetimeString(promoCode.expiresAt) : '',
    isActive: promoCode.isActive ?? false,
    firstUseOnly: promoCode.firstUseOnly ?? false,
    appliesToPasses: promoCode.appliesToPasses ?? false,
  });

  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code || !formData.discountValue) {
      toast({
        title: 'Validation Error',
        description: 'Code and discount value are required.',
        variant: 'destructive',
      });
      return;
    }
    const expiresAt: string | null = formData.expiresAt
      ? new Date(formData.expiresAt).toISOString()
      : null;
    onUpdate({
      ...formData,
      expiresAt,
      maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
      minimumAmount: formData.minimumAmount || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-code">Promo Code *</Label>
          <Input
            id="edit-code"
            value={formData.code}
            onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-discountType">Discount Type</Label>
          <Select
            value={formData.discountType}
            onValueChange={(value: 'percentage' | 'fixed') =>
              setFormData({ ...formData, discountType: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage (%)</SelectItem>
              <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-discountValue">
            Discount Value * {formData.discountType === 'percentage' ? '(%)' : '($)'}
          </Label>
          <Input
            id="edit-discountValue"
            type="number"
            step={formData.discountType === 'percentage' ? '1' : '0.01'}
            min="0"
            max={formData.discountType === 'percentage' ? '100' : undefined}
            value={formData.discountValue}
            onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-minimumAmount">
            Minimum Amount ($){' '}
            <span className="text-muted-foreground">(optional - no minimum if empty)</span>
          </Label>
          <Input
            id="edit-minimumAmount"
            type="number"
            step="0.01"
            min="0"
            placeholder="No minimum amount"
            value={formData.minimumAmount}
            onChange={e => setFormData({ ...formData, minimumAmount: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-maxUses">
            Max Uses <span className="text-muted-foreground">(optional - unlimited if empty)</span>
          </Label>
          <Input
            id="edit-maxUses"
            type="number"
            min="1"
            placeholder="Unlimited uses"
            value={formData.maxUses}
            onChange={e => setFormData({ ...formData, maxUses: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <DateTimePicker
            id="edit-expiresAt"
            label="Expires At"
            value={formData.expiresAt}
            onChange={value => setFormData({ ...formData, expiresAt: value })}
            placeholder="Optional - never expires if empty"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="edit-description">Description</Label>
        <Textarea
          id="edit-description"
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="edit-isActive"
          checked={formData.isActive ?? false}
          onCheckedChange={checked => setFormData({ ...formData, isActive: checked })}
        />
        <Label htmlFor="edit-isActive">Active</Label>
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          id="edit-firstUseOnly"
          checked={formData.firstUseOnly}
          onCheckedChange={checked => setFormData({ ...formData, firstUseOnly: checked === true })}
        />
        <Label htmlFor="edit-firstUseOnly" className="cursor-pointer">
          First use only (one per customer)
        </Label>
      </div>

      <div className="flex items-center gap-3">
        <Checkbox
          id="edit-appliesToPasses"
          checked={formData.appliesToPasses}
          onCheckedChange={checked =>
            setFormData({ ...formData, appliesToPasses: checked === true })
          }
        />
        <Label htmlFor="edit-appliesToPasses" className="cursor-pointer">
          Can be used on punch passes
        </Label>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading ? 'Updating...' : 'Update Promo Code'}
        </Button>
        <Button type="button" variant="outline" onClick={onSuccess}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ─── Locations Tab ────────────────────────────────────────────────────────────

interface LocationRow {
  id: string;
  localityId: string | null;
  localityName: string | null;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  description: string | null;
  website: string | null;
  phoneNumber: string | null;
  email: string | null;
  hours: string | null;
  thumbnailUrl: string | null;
  isActive: boolean;
  ownerUserId: string | null;
  ownerEmail: string | null;
  payoutMode: 'connect' | 'platform';
}

interface VenueOwnerOption {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface LocalityOption {
  id: string;
  name: string;
  slug: string;
}

interface LocationFormState {
  name: string;
  localityId: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  description: string;
  website: string;
  phoneNumber: string;
  email: string;
  hours: string;
  thumbnailUrl: string;
  ownerUserId: string;
  payoutMode: 'connect' | 'platform';
}

const emptyLocationForm: LocationFormState = {
  name: '',
  localityId: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  description: '',
  website: '',
  phoneNumber: '',
  email: 'admin@kalovate.com',
  hours: '',
  thumbnailUrl: '',
  ownerUserId: '',
  payoutMode: 'connect',
};

function LocationsTab() {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationRow | null>(null);
  const [form, setForm] = useState<LocationFormState>(emptyLocationForm);
  const [localityFilter, setLocalityFilter] = useState('');

  const { data: locations = [], refetch } = useQuery<LocationRow[]>({
    queryKey: ['/api/admin/locations', localityFilter],
    queryFn: () => {
      const params = localityFilter ? `?localityId=${localityFilter}` : '';
      return authedJson<LocationRow[]>(`/api/admin/locations${params}`);
    },
  });

  const { data: localities = [] } = useQuery<LocalityOption[]>({
    queryKey: ['/api/regions'],
    queryFn: async () => {
      const regions =
        await fetchJson<Array<{ id: string; type: string; slug: string }>>('/api/regions');
      const physicalRegions = regions.filter(r => r.type === 'physical');
      const results: LocalityOption[] = [];
      for (const region of physicalRegions) {
        const locs = await fetchJson<LocalityOption[]>(`/api/regions/${region.slug}/localities`);
        results.push(...locs);
      }
      return results;
    },
  });

  // Users with the venue role — candidates for venue ownership/payouts.
  const { data: venueOwners = [] } = useQuery<VenueOwnerOption[]>({
    queryKey: ['/api/admin/users', 'venue-role'],
    queryFn: () => authedJson<VenueOwnerOption[]>('/api/admin/users?role=venue&limit=100'),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: LocationFormState & { id?: string; isActive?: boolean }) => {
      const { id, ownerUserId, ...body } = data;
      const url = id ? `/api/admin/locations/${id}` : '/api/admin/locations';
      const method = id ? 'PUT' : 'POST';
      // Empty string means "no owner" — the API expects null.
      return apiRequest(method, url, { ...body, ownerUserId: ownerUserId || null });
    },
    onSuccess: () => {
      toast({ title: editingLocation ? 'Location updated' : 'Location created' });
      setShowForm(false);
      setEditingLocation(null);
      setForm(emptyLocationForm);
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/locations/${id}`),
    onSuccess: () => {
      toast({ title: 'Location deactivated' });
      refetch();
    },
  });

  function openCreate() {
    setEditingLocation(null);
    setForm(emptyLocationForm);
    setShowForm(true);
  }

  function openEdit(loc: LocationRow) {
    setEditingLocation(loc);
    setForm({
      name: loc.name,
      localityId: loc.localityId ?? '',
      address: loc.address ?? '',
      city: loc.city ?? '',
      state: loc.state ?? '',
      zip: loc.zip ?? '',
      description: loc.description ?? '',
      website: loc.website ?? '',
      phoneNumber: loc.phoneNumber ?? '',
      email: 'admin@kalovate.com',
      hours: loc.hours ?? '',
      thumbnailUrl: loc.thumbnailUrl ?? '',
      ownerUserId: loc.ownerUserId ?? '',
      payoutMode: loc.payoutMode ?? 'connect',
    });
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(editingLocation ? { ...form, id: editingLocation.id } : form);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Class Locations</h2>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Location
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Select
          value={localityFilter || SELECT_ALL}
          onValueChange={value => setLocalityFilter(value === SELECT_ALL ? '' : value)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All localities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL}>All localities</SelectItem>
            {localities.map(l => (
              <SelectItem key={l.id} value={l.id}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingLocation ? 'Edit Location' : 'New Location'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="loc-name">Name *</Label>
                  <Input
                    id="loc-name"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="loc-locality">Locality *</Label>
                  <Select
                    value={form.localityId}
                    onValueChange={v => setForm({ ...form, localityId: v })}
                  >
                    <SelectTrigger id="loc-locality">
                      <SelectValue placeholder="Select locality" />
                    </SelectTrigger>
                    <SelectContent>
                      {localities.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="loc-address">Address</Label>
                <Input
                  id="loc-address"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="loc-city">City</Label>
                  <Input
                    id="loc-city"
                    value={form.city}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="loc-state">State</Label>
                  <Input
                    id="loc-state"
                    value={form.state}
                    onChange={e => setForm({ ...form, state: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="loc-zip">ZIP</Label>
                  <Input
                    id="loc-zip"
                    value={form.zip}
                    onChange={e => setForm({ ...form, zip: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="loc-description">Description</Label>
                <Textarea
                  id="loc-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="loc-website">Website</Label>
                  <Input
                    id="loc-website"
                    value={form.website}
                    onChange={e => setForm({ ...form, website: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="loc-email">Contact Email</Label>
                  <Input id="loc-email" value="admin@kalovate.com" readOnly />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="loc-thumbnail">Thumbnail URL</Label>
                  <Input
                    id="loc-thumbnail"
                    value={form.thumbnailUrl}
                    onChange={e => setForm({ ...form, thumbnailUrl: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="loc-owner">Venue Owner (receives venue payout share)</Label>
                <Select
                  value={form.ownerUserId || SELECT_ALL}
                  onValueChange={v => setForm({ ...form, ownerUserId: v === SELECT_ALL ? '' : v })}
                >
                  <SelectTrigger id="loc-owner" data-testid="select-location-owner">
                    <SelectValue placeholder="No owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>No owner</SelectItem>
                    {venueOwners.map(owner => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {[owner.firstName, owner.lastName].filter(Boolean).join(' ') || owner.email}{' '}
                        ({owner.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only users with the Venue role appear here. Set a user's role to Venue in the
                  Class Dashboard first.
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="loc-payout-mode">Venue payout routing</Label>
                <Select
                  value={form.payoutMode}
                  onValueChange={(v: 'connect' | 'platform') => setForm({ ...form, payoutMode: v })}
                >
                  <SelectTrigger id="loc-payout-mode" data-testid="select-location-payout-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="connect">
                      Pay venue owner directly (Stripe Connect)
                    </SelectItem>
                    <SelectItem value="platform">Fold into Kalovate's payout</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  "Fold into Kalovate's payout" skips a separate venue transfer entirely — the
                  venue's share of every sale stays in Kalovate's cut instead, even if the fee
                  profile for a class here sets a venue %. Use this for venues that don't have (or
                  don't want) their own connected Stripe account.
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={saveMutation.isPending} className="flex-1">
                  {saveMutation.isPending
                    ? 'Saving...'
                    : editingLocation
                      ? 'Update Location'
                      : 'Create Location'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLocation(null);
                    setForm(emptyLocationForm);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Locality</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No locations yet
                  </TableCell>
                </TableRow>
              ) : (
                locations.map(loc => (
                  <TableRow key={loc.id}>
                    <TableCell className="font-medium">{loc.name}</TableCell>
                    <TableCell>{loc.localityName ?? '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {loc.ownerEmail ?? '—'}
                      {loc.ownerEmail && loc.payoutMode === 'platform' && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Folded into Kalovate
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={loc.isActive ? 'default' : 'secondary'}>
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(loc)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {loc.isActive && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deactivateMutation.mutate(loc.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Blog Admin Tab ───────────────────────────────────────────────────────────

interface BlogPostRow {
  id: string;
  title: string;
  slug: string;
  status: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  regionId: string | null;
  localityId: string | null;
  excerpt: string | null;
  featuredImageUrl: string | null;
  authorId: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  isPinned: boolean;
}

interface BlogWriterRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isBlogWriter: boolean;
}

interface BlogCategoryRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

const STATUS_LABELS: Record<
  string,
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  draft: { label: 'Draft', variant: 'secondary' },
  pending_review: { label: 'Pending Review', variant: 'outline' },
  changes_requested: { label: 'Changes Requested', variant: 'destructive' },
  published: { label: 'Published', variant: 'default' },
  scheduled: { label: 'Scheduled', variant: 'outline' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  archived: { label: 'Archived', variant: 'secondary' },
};

// ─── Calendar Admin Tab ──────────────────────────────────────────────────────

const CATEGORY_LABELS_ADMIN: Record<string, string> = {
  class_session: 'Class Session',
  farmers_market: 'Farmers Market',
  community: 'Community',
  workshop: 'Workshop',
  arts: 'Arts',
  sports: 'Sports',
  food_drink: 'Food & Drink',
  outdoor: 'Outdoor',
  government: 'Government',
  wellness: 'Wellness',
  music: 'Music',
  other: 'Other',
};

interface AdminCalendarEvent {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  category: string;
  attendanceMode: string;
  eventStatus: string;
  isPublished: boolean;
  isFeatured: boolean;
  externalUrl: string | null;
  regionId: string | null;
  localityId: string | null;
  venueId: string | null;
  venueName: string | null;
  externalFeedId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdminCalendarEventFull extends AdminCalendarEvent {
  description: string | null;
  tags: string[];
}

interface AdminLocality {
  id: string;
  name: string;
  slug: string;
  regionId: string;
  regionName: string | null;
}

interface AdminVenue {
  id: string;
  name: string;
  slug: string;
  localityId: string | null;
}

interface AdminFeed {
  id: string;
  name: string;
  icalUrl: string;
  defaultCategory: string;
  isActive: boolean;
  lastSyncedAt: string | null;
  lastSyncCount: number | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  localityId: string | null;
}

interface AdminLocation {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  localityId: string | null;
  localityName: string | null;
  isActive: boolean;
}

interface AdminSeries {
  id: string;
  title: string;
  category: string;
  rrule: string;
  dtstart: string;
  timezone: string;
  durationMinutes: number;
  status: string;
  isActive: boolean;
  localityId: string | null;
  localityName: string | null;
  regionId: string | null;
  createdAt: string;
  updatedAt: string;
}

const BLANK_SERIES_FORM = {
  title: '',
  description: '',
  category: 'community' as string,
  rrule: 'FREQ=WEEKLY;BYDAY=MO',
  dtstart: '',
  timezone: 'America/Indiana/Indianapolis',
  durationMinutes: 60,
  localityId: '',
  regionId: '',
  externalUrl: '',
};

const BLANK_EVENT_FORM = {
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  localityId: '',
  venueId: '',
  category: 'community' as string,
  attendanceMode: 'in_person' as string,
  eventStatus: 'scheduled' as string,
  externalUrl: '',
  isPublished: false,
  isFeatured: false,
};

const BLANK_FEED_FORM = {
  name: '',
  icalUrl: '',
  localityId: '',
  defaultCategory: 'community' as string,
  sourceName: '',
  isActive: true,
};

const BLANK_LOCATION_FORM = {
  name: '',
  slug: '',
  address: '',
  localityId: '',
  isActive: true,
};

function CalendarAdminTab() {
  const { toast } = useToast();
  const [calTab, setCalTab] = useState<'events' | 'feeds' | 'locations' | 'series' | 'submissions'>(
    'events'
  );

  // ── Event form state ──
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(BLANK_EVENT_FORM);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ── Feed form state ──
  const [feedFormOpen, setFeedFormOpen] = useState(false);
  const [editingFeedId, setEditingFeedId] = useState<string | null>(null);
  const [feedForm, setFeedForm] = useState(BLANK_FEED_FORM);

  // ── Location form state ──
  const [locFormOpen, setLocFormOpen] = useState(false);
  const [editingLocId, setEditingLocId] = useState<string | null>(null);
  const [locForm, setLocForm] = useState(BLANK_LOCATION_FORM);

  // ── Series form state ──
  const [seriesFormOpen, setSeriesFormOpen] = useState(false);
  const [seriesForm, setSeriesForm] = useState(BLANK_SERIES_FORM);

  // ── Series split ("this and future") state ──
  const [splitDialog, setSplitDialog] = useState<{
    open: boolean;
    seriesId: string | null;
    seriesTitle: string;
  }>({ open: false, seriesId: null, seriesTitle: '' });
  const [splitTargetOriginalStartsAt, setSplitTargetOriginalStartsAt] = useState<string | null>(
    null
  );

  // ── Embed snippet generator state ──
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedLocality, setEmbedLocality] = useState('');
  const [embedLimit, setEmbedLimit] = useState('10');
  const [embedCopied, setEmbedCopied] = useState(false);

  const { data: eventsData, refetch: refetchEvents } = useQuery<{ data: AdminCalendarEvent[] }>({
    queryKey: ['admin-calendar-events'],
    queryFn: () => authedJson<{ data: AdminCalendarEvent[] }>('/api/admin/calendar/events'),
  });

  const { data: localitiesData } = useQuery<{ data: AdminLocality[] }>({
    queryKey: ['admin-calendar-localities'],
    queryFn: () => authedJson<{ data: AdminLocality[] }>('/api/admin/calendar/localities'),
  });

  const { data: venuesData } = useQuery<{ data: AdminVenue[] }>({
    queryKey: ['admin-calendar-venues'],
    queryFn: () => authedJson<{ data: AdminVenue[] }>('/api/admin/calendar/venues'),
  });

  const { data: feedsData, refetch: refetchFeeds } = useQuery<{ data: AdminFeed[] }>({
    queryKey: ['admin-calendar-feeds'],
    queryFn: () => authedJson<{ data: AdminFeed[] }>('/api/admin/calendar/feeds'),
  });

  const { data: locationsData, refetch: refetchLocations } = useQuery<{ data: AdminLocation[] }>({
    queryKey: ['admin-calendar-locations'],
    queryFn: () => authedJson<{ data: AdminLocation[] }>('/api/admin/calendar/locations'),
  });

  const { data: seriesData, refetch: refetchSeries } = useQuery<{ data: AdminSeries[] }>({
    queryKey: ['admin-calendar-series'],
    queryFn: () => authedJson<{ data: AdminSeries[] }>('/api/admin/calendar/series'),
  });

  // ── Event conflict check (debounced via key) ──
  const conflictEnabled = formOpen && !!form.startsAt && !!(form.venueId || form.localityId);

  interface ConflictResult {
    venueConflicts: { id: string; title: string; startsAt: string; venueName: string | null }[];
    localityConflicts: {
      id: string;
      title: string;
      startsAt: string;
      localityName: string | null;
    }[];
  }

  const { data: conflictData } = useQuery<ConflictResult>({
    queryKey: [
      'admin-calendar-conflict',
      form.startsAt,
      form.endsAt,
      form.venueId,
      form.localityId,
      editingId,
    ],
    queryFn: () =>
      authedJson<ConflictResult>('/api/admin/calendar/conflict-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startsAt: new Date(form.startsAt).toISOString(),
          endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
          venueId: form.venueId || undefined,
          localityId: form.localityId || undefined,
          excludeId: editingId ?? undefined,
        }),
      }),
    enabled: conflictEnabled,
    staleTime: 30_000,
  });

  const hasVenueConflict = (conflictData?.venueConflicts?.length ?? 0) > 0;
  const hasLocalityConflict = (conflictData?.localityConflicts?.length ?? 0) > 0;

  // ── Submissions ──
  const [showAllSubmissions, setShowAllSubmissions] = useState(false);
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    submissionId: string | null;
    decision: 'approve' | 'reject';
    eventTitle: string;
    eventStartsAt: string | null;
    eventEndsAt: string | null;
    eventVenueId: string | null;
    localityId: string | null;
    eventId: string | null;
  }>({
    open: false,
    submissionId: null,
    decision: 'approve',
    eventTitle: '',
    eventStartsAt: null,
    eventEndsAt: null,
    eventVenueId: null,
    localityId: null,
    eventId: null,
  });
  const [reviewNotes, setReviewNotes] = useState('');

  // Conflict check for submission approval — runs after reviewDialog is declared
  const submissionConflictEnabled =
    reviewDialog.open &&
    reviewDialog.decision === 'approve' &&
    !!reviewDialog.eventStartsAt &&
    !!(reviewDialog.eventVenueId || reviewDialog.localityId);

  const { data: submissionConflictData } = useQuery<ConflictResult>({
    queryKey: [
      'admin-calendar-submission-conflict',
      reviewDialog.eventStartsAt,
      reviewDialog.eventEndsAt,
      reviewDialog.eventVenueId,
      reviewDialog.localityId,
      reviewDialog.eventId,
    ],
    queryFn: () =>
      authedJson<ConflictResult>('/api/admin/calendar/conflict-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startsAt: new Date(reviewDialog.eventStartsAt!).toISOString(),
          endsAt: reviewDialog.eventEndsAt
            ? new Date(reviewDialog.eventEndsAt).toISOString()
            : undefined,
          venueId: reviewDialog.eventVenueId || undefined,
          localityId: reviewDialog.localityId || undefined,
          excludeId: reviewDialog.eventId ?? undefined,
        }),
      }),
    enabled: submissionConflictEnabled,
    staleTime: 30_000,
  });

  const submissionHasVenueConflict = (submissionConflictData?.venueConflicts?.length ?? 0) > 0;
  const submissionHasLocalityConflict =
    (submissionConflictData?.localityConflicts?.length ?? 0) > 0;

  interface AdminSubmission {
    id: string;
    eventId: string | null;
    submitterName: string;
    submitterEmail: string;
    submitterMessage: string | null;
    reviewDecision: string | null;
    reviewNotes: string | null;
    reviewedAt: string | null;
    createdAt: string;
    eventTitle: string | null;
    eventCategory: string | null;
    eventStartsAt: string | null;
    eventEndsAt: string | null;
    eventStatus: string | null;
    eventIsPublished: boolean | null;
    localityId: string | null;
  }

  const { data: submissionsData, refetch: refetchSubmissions } = useQuery<{
    data: AdminSubmission[];
  }>({
    queryKey: ['admin-calendar-submissions', showAllSubmissions],
    queryFn: () =>
      authedJson<{ data: AdminSubmission[] }>(
        `/api/admin/calendar/submissions?pending=${showAllSubmissions ? 'false' : 'true'}`
      ),
    enabled: calTab === 'submissions',
  });

  const approveSubmissionMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const res = await authedFetch(`/api/admin/calendar/submissions/${id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Submission approved and published' });
      setReviewDialog(d => ({ ...d, open: false }));
      setReviewNotes('');
      refetchSubmissions();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const rejectSubmissionMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const res = await authedFetch(`/api/admin/calendar/submissions/${id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Submission rejected' });
      setReviewDialog(d => ({ ...d, open: false }));
      setReviewNotes('');
      refetchSubmissions();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const createSeriesMutation = useMutation({
    mutationFn: async (data: typeof seriesForm) => {
      const body = {
        ...data,
        localityId: data.localityId || null,
        regionId: data.regionId || null,
        externalUrl: data.externalUrl || null,
        durationMinutes: Number(data.durationMinutes),
      };
      const res = await authedFetch('/api/admin/calendar/series', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Series created' });
      setSeriesFormOpen(false);
      setSeriesForm(BLANK_SERIES_FORM);
      refetchSeries();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const publishSeriesMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/series/${id}/publish`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Series published' });
      refetchSeries();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const materializeSeriesMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/series/${id}/materialize`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{ count: number }>;
    },
    onSuccess: result => {
      toast({ title: `Materialized ${result.count} instances` });
      refetchSeries();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const archiveSeriesMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/series/${id}/archive`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Series archived' });
      refetchSeries();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const { data: splitInstancesData } = useQuery<{
    data: {
      id: string;
      originalStartsAt: string;
      startsAt: string;
      endsAt: string;
      status: string;
      isException: boolean;
    }[];
  }>({
    queryKey: ['admin-calendar-series-instances', splitDialog.seriesId],
    queryFn: () =>
      authedJson<{
        data: {
          id: string;
          originalStartsAt: string;
          startsAt: string;
          endsAt: string;
          status: string;
          isException: boolean;
        }[];
      }>(`/api/admin/calendar/series/${splitDialog.seriesId}/instances`),
    enabled: splitDialog.open && !!splitDialog.seriesId,
  });

  const splitSeriesMutation = useMutation({
    mutationFn: async ({
      seriesId,
      originalStartsAt,
    }: {
      seriesId: string;
      originalStartsAt: string;
    }) => {
      const res = await authedFetch(`/api/admin/calendar/series/${seriesId}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originalStartsAt }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (data: { newSeriesId: string }) => {
      toast({
        title: 'Series split',
        description: `New series created (ID: ${data.newSeriesId.slice(0, 8)}…). Edit it to make changes for this and future instances.`,
      });
      setSplitDialog({ open: false, seriesId: null, seriesTitle: '' });
      setSplitTargetOriginalStartsAt(null);
      refetchSeries();
    },
    onError: (err: Error) =>
      toast({ title: 'Split failed', description: err.message, variant: 'destructive' }),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const url = editingId
        ? `/api/admin/calendar/events/${editingId}`
        : '/api/admin/calendar/events';
      const method = editingId ? 'PUT' : 'POST';
      const body = {
        ...data,
        startsAt: data.startsAt ? new Date(data.startsAt).toISOString() : undefined,
        endsAt: data.endsAt ? new Date(data.endsAt).toISOString() : null,
        localityId: data.localityId || null,
        venueId: data.venueId || null,
        externalUrl: data.externalUrl || null,
      };
      const res = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editingId ? 'Event updated' : 'Event created' });
      setFormOpen(false);
      setEditingId(null);
      setForm(BLANK_EVENT_FORM);
      refetchEvents();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) => {
      const res = await authedFetch(
        `/api/admin/calendar/events/${id}/${publish ? 'publish' : 'unpublish'}`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => refetchEvents(),
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const featureMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/events/${id}/feature`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => refetchEvents(),
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/events/${id}/duplicate`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Event duplicated' });
      refetchEvents();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/events/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => {
      toast({ title: 'Event removed' });
      setDeleteConfirmId(null);
      refetchEvents();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const saveFeedMutation = useMutation({
    mutationFn: async (data: typeof feedForm) => {
      const url = editingFeedId
        ? `/api/admin/calendar/feeds/${editingFeedId}`
        : '/api/admin/calendar/feeds';
      const method = editingFeedId ? 'PUT' : 'POST';
      const body = {
        ...data,
        localityId: data.localityId || null,
        sourceName: data.sourceName || null,
      };
      const res = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editingFeedId ? 'Feed updated' : 'Feed added' });
      setFeedFormOpen(false);
      setEditingFeedId(null);
      setFeedForm(BLANK_FEED_FORM);
      refetchFeeds();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const syncFeedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/feeds/${id}/sync`, { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Sync failed' }));
        throw new Error(body.detail ?? body.error ?? 'Sync failed');
      }
      return res.json() as Promise<{ synced: number; skippedRecurring: number; total: number }>;
    },
    onSuccess: result => {
      toast({
        title: `Synced ${result.synced} events${result.skippedRecurring ? ` (${result.skippedRecurring} recurring skipped)` : ''}`,
      });
      refetchFeeds();
      refetchEvents();
    },
    onError: (err: Error) =>
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' }),
  });

  const deleteFeedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/admin/calendar/feeds/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => {
      toast({ title: 'Feed removed' });
      refetchFeeds();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const saveLocMutation = useMutation({
    mutationFn: async (data: typeof locForm) => {
      const url = editingLocId
        ? `/api/admin/calendar/locations/${editingLocId}`
        : '/api/admin/calendar/locations';
      const method = editingLocId ? 'PUT' : 'POST';
      const body = { ...data, localityId: data.localityId || null, address: data.address || null };
      const res = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editingLocId ? 'Location updated' : 'Location created' });
      setLocFormOpen(false);
      setEditingLocId(null);
      setLocForm(BLANK_LOCATION_FORM);
      refetchLocations();
      refetchEvents();
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  async function openEdit(ev: AdminCalendarEvent) {
    // Fetch full event to get description
    const res = await fetch(`/api/admin/calendar/events/${ev.id}`);
    const full: { data: AdminCalendarEventFull } = await res.json();
    const d = full.data;
    setEditingId(ev.id);
    setForm({
      title: d.title,
      description: d.description ?? '',
      startsAt: d.startsAt ? new Date(d.startsAt).toISOString().slice(0, 16) : '',
      endsAt: d.endsAt ? new Date(d.endsAt).toISOString().slice(0, 16) : '',
      localityId: d.localityId ?? '',
      venueId: d.venueId ?? '',
      category: d.category,
      attendanceMode: d.attendanceMode,
      eventStatus: d.eventStatus,
      externalUrl: d.externalUrl ?? '',
      isPublished: d.isPublished,
      isFeatured: d.isFeatured,
    });
    setFormOpen(true);
  }

  function openCreate() {
    setEditingId(null);
    setForm(BLANK_EVENT_FORM);
    setFormOpen(true);
  }

  function openEditFeed(feed: AdminFeed) {
    setEditingFeedId(feed.id);
    setFeedForm({
      name: feed.name,
      icalUrl: feed.icalUrl,
      localityId: feed.localityId ?? '',
      defaultCategory: feed.defaultCategory,
      sourceName: '',
      isActive: feed.isActive,
    });
    setFeedFormOpen(true);
  }

  function openEditLocation(loc: AdminLocation) {
    setEditingLocId(loc.id);
    setLocForm({
      name: loc.name,
      slug: loc.slug,
      address: loc.address ?? '',
      localityId: loc.localityId ?? '',
      isActive: loc.isActive,
    });
    setLocFormOpen(true);
  }

  const events = eventsData?.data ?? [];
  const localities = localitiesData?.data ?? [];
  const venues = venuesData?.data ?? [];
  const feeds = feedsData?.data ?? [];
  const locations = locationsData?.data ?? [];
  const seriesList = seriesData?.data ?? [];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Calendar</h2>

      <Tabs value={calTab} onValueChange={v => setCalTab(v as typeof calTab)}>
        <div className="overflow-x-auto overscroll-x-contain">
          <TabsList className="inline-flex w-max">
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="feeds">External Feeds</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            <TabsTrigger value="series">Recurring Series</TabsTrigger>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
          </TabsList>
        </div>

        {/* ── Events tab ─────────────────────────────────────────────────── */}
        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEmbedDialogOpen(true)} className="gap-2">
              <Code2 className="h-4 w-4" />
              Get embed code
            </Button>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              New Event
            </Button>
          </div>

          {/* Embed snippet generator dialog */}
          <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Calendar Widget Embed Code</DialogTitle>
                <DialogDescription>
                  Copy this snippet and paste it into any webpage to show upcoming events.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Locality (optional)</Label>
                  <Select
                    value={embedLocality || SELECT_ALL}
                    onValueChange={value => setEmbedLocality(value === SELECT_ALL ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All localities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_ALL}>All localities</SelectItem>
                      {localities.map(l => (
                        <SelectItem key={l.id} value={l.slug}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Max events shown</Label>
                  <Select value={embedLimit} onValueChange={setEmbedLimit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['5', '10', '15', '20', '25'].map(n => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Embed code</Label>
                  <div className="relative">
                    <pre className="rounded-md bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
                      {`<iframe\n  src="https://kalovate.com/widget/calendar?${new URLSearchParams(
                        Object.fromEntries(
                          [
                            embedLocality ? ['locality', embedLocality] : null,
                            ['limit', embedLimit],
                          ].filter(Boolean) as [string, string][]
                        )
                      ).toString()}"\n  style="border:0;width:100%;height:480px"\n  loading="lazy"\n></iframe>`}
                    </pre>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute top-2 right-2 h-7 w-7"
                      onClick={() => {
                        const snippet = `<iframe\n  src="https://kalovate.com/widget/calendar?${new URLSearchParams(
                          Object.fromEntries(
                            [
                              embedLocality ? ['locality', embedLocality] : null,
                              ['limit', embedLimit],
                            ].filter(Boolean) as [string, string][]
                          )
                        ).toString()}"\n  style="border:0;width:100%;height:480px"\n  loading="lazy"\n></iframe>`;
                        navigator.clipboard.writeText(snippet).then(() => {
                          setEmbedCopied(true);
                          setTimeout(() => setEmbedCopied(false), 2000);
                        });
                      }}
                      aria-label="Copy embed code"
                    >
                      {embedCopied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmbedDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Event form dialog */}
          <Dialog
            open={formOpen}
            onOpenChange={open => {
              setFormOpen(open);
              if (!open) setEditingId(null);
            }}
          >
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Event' : 'Create Event'}</DialogTitle>
                <DialogDescription>Fill in the event details below.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Title *</Label>
                  <Input
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Event title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Starts at *</Label>
                    <Input
                      type="datetime-local"
                      value={form.startsAt}
                      onChange={e => setForm(f => ({ ...f, startsAt: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ends at</Label>
                    <Input
                      type="datetime-local"
                      value={form.endsAt}
                      onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Category</Label>
                    <Select
                      value={form.category}
                      onValueChange={v => setForm(f => ({ ...f, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CALENDAR_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {CATEGORY_LABELS_ADMIN[cat] ?? cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <Select
                      value={form.eventStatus}
                      onValueChange={v => setForm(f => ({ ...f, eventStatus: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="postponed">Postponed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Attendance Mode</Label>
                  <Select
                    value={form.attendanceMode}
                    onValueChange={v => setForm(f => ({ ...f, attendanceMode: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">In Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Locality</Label>
                  <Select
                    value={form.localityId || SELECT_NONE}
                    onValueChange={v =>
                      setForm(f => ({ ...f, localityId: v === SELECT_NONE ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select locality" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                      {localities.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} ({l.regionName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Venue</Label>
                  <Select
                    value={form.venueId || SELECT_NONE}
                    onValueChange={v =>
                      setForm(f => ({ ...f, venueId: v === SELECT_NONE ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                      {venues.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>External URL</Label>
                  <Input
                    value={form.externalUrl}
                    onChange={e => setForm(f => ({ ...f, externalUrl: e.target.value }))}
                    placeholder="https://..."
                    type="url"
                  />
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is-published"
                      checked={form.isPublished}
                      onCheckedChange={v => setForm(f => ({ ...f, isPublished: v }))}
                    />
                    <Label htmlFor="is-published">Published</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is-featured"
                      checked={form.isFeatured}
                      onCheckedChange={v => setForm(f => ({ ...f, isFeatured: v }))}
                    />
                    <Label htmlFor="is-featured">Featured</Label>
                  </div>
                </div>
              </div>
              {/* Conflict warnings */}
              {hasVenueConflict && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 space-y-1">
                  <p className="text-sm font-medium text-destructive">Same-venue conflict</p>
                  {conflictData?.venueConflicts.map(c => (
                    <p key={c.id} className="text-xs text-destructive/80">
                      "{c.title}" at{' '}
                      {new Date(c.startsAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  ))}
                </div>
              )}
              {!hasVenueConflict && hasLocalityConflict && (
                <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 space-y-1">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    Nearby same-area events
                  </p>
                  {conflictData?.localityConflicts.map(c => (
                    <p key={c.id} className="text-xs text-amber-600 dark:text-amber-500">
                      "{c.title}" at{' '}
                      {new Date(c.startsAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setFormOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={!form.title.trim() || !form.startsAt || saveMutation.isPending}
                  onClick={() => saveMutation.mutate(form)}
                >
                  {saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Event'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete confirmation dialog */}
          <Dialog
            open={!!deleteConfirmId}
            onOpenChange={open => {
              if (!open) setDeleteConfirmId(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove event?</DialogTitle>
                <DialogDescription>
                  This will unpublish and mark the event as cancelled. It will no longer appear on
                  the public calendar.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
                >
                  {deleteMutation.isPending ? 'Removing…' : 'Remove'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No events yet. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map(ev => (
                    <TableRow key={ev.id}>
                      <TableCell className="font-medium">
                        <span>{ev.title}</span>
                        {ev.isFeatured ? (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Featured
                          </Badge>
                        ) : null}
                        {ev.externalFeedId ? (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Feed
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {new Date(ev.startsAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {CATEGORY_LABELS_ADMIN[ev.category] ?? ev.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ev.venueName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={ev.isPublished ? 'default' : 'secondary'}>
                          {ev.eventStatus === 'cancelled'
                            ? 'Cancelled'
                            : ev.isPublished
                              ? 'Published'
                              : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title={ev.isPublished ? 'Unpublish' : 'Publish'}
                            onClick={() =>
                              publishMutation.mutate({ id: ev.id, publish: !ev.isPublished })
                            }
                          >
                            {ev.isPublished ? 'Unpublish' : 'Publish'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title={ev.isFeatured ? 'Unfeature' : 'Feature'}
                            onClick={() => featureMutation.mutate(ev.id)}
                          >
                            <Star
                              className={`h-3.5 w-3.5 ${ev.isFeatured ? 'fill-primary text-primary' : ''}`}
                            />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Duplicate"
                            onClick={() => duplicateMutation.mutate(ev.id)}
                          >
                            <Shuffle className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(ev)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirmId(ev.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── Feeds tab ──────────────────────────────────────────────────── */}
        <TabsContent value="feeds" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setEditingFeedId(null);
                setFeedForm(BLANK_FEED_FORM);
                setFeedFormOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Feed
            </Button>
          </div>

          <Dialog
            open={feedFormOpen}
            onOpenChange={open => {
              setFeedFormOpen(open);
              if (!open) setEditingFeedId(null);
            }}
          >
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingFeedId ? 'Edit Feed' : 'Add External Feed'}</DialogTitle>
                <DialogDescription>
                  Configure an ICS calendar feed to import events automatically.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Feed name *</Label>
                  <Input
                    value={feedForm.name}
                    onChange={e => setFeedForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Monroe County Farmers Markets"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>ICS URL *</Label>
                  <Input
                    value={feedForm.icalUrl}
                    onChange={e => setFeedForm(f => ({ ...f, icalUrl: e.target.value }))}
                    placeholder="https://example.com/events.ics"
                    type="url"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Source label</Label>
                  <Input
                    value={feedForm.sourceName}
                    onChange={e => setFeedForm(f => ({ ...f, sourceName: e.target.value }))}
                    placeholder="City of Bloomington"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Locality</Label>
                    <Select
                      value={feedForm.localityId || SELECT_NONE}
                      onValueChange={v =>
                        setFeedForm(f => ({ ...f, localityId: v === SELECT_NONE ? '' : v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select locality" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                        {localities.map(l => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Default category</Label>
                    <Select
                      value={feedForm.defaultCategory}
                      onValueChange={v => setFeedForm(f => ({ ...f, defaultCategory: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CALENDAR_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {CATEGORY_LABELS_ADMIN[cat] ?? cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="feed-active"
                    checked={feedForm.isActive}
                    onCheckedChange={v => setFeedForm(f => ({ ...f, isActive: v }))}
                  />
                  <Label htmlFor="feed-active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setFeedFormOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    !feedForm.name.trim() || !feedForm.icalUrl.trim() || saveFeedMutation.isPending
                  }
                  onClick={() => saveFeedMutation.mutate(feedForm)}
                >
                  {saveFeedMutation.isPending
                    ? 'Saving…'
                    : editingFeedId
                      ? 'Save Changes'
                      : 'Add Feed'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No feeds configured. Add one to start importing events.
                    </TableCell>
                  </TableRow>
                ) : (
                  feeds.map(feed => (
                    <TableRow key={feed.id}>
                      <TableCell>
                        <div className="font-medium">{feed.name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[280px]">
                          {feed.icalUrl}
                        </div>
                        {feed.lastSyncError ? (
                          <div className="text-xs text-destructive mt-0.5 truncate max-w-[280px]">
                            {feed.lastSyncError}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {feed.lastSyncedAt
                          ? `${new Date(feed.lastSyncedAt).toLocaleDateString()} · ${feed.lastSyncCount ?? 0} events`
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            feed.lastSyncStatus === 'error'
                              ? 'destructive'
                              : feed.lastSyncStatus === 'ok'
                                ? 'default'
                                : 'secondary'
                          }
                        >
                          {feed.lastSyncStatus ?? 'not synced'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            disabled={syncFeedMutation.isPending}
                            onClick={() => syncFeedMutation.mutate(feed.id)}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Sync
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditFeed(feed)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteFeedMutation.mutate(feed.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── Locations tab ──────────────────────────────────────────────── */}
        <TabsContent value="locations" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setEditingLocId(null);
                setLocForm(BLANK_LOCATION_FORM);
                setLocFormOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Location
            </Button>
          </div>

          <Dialog
            open={locFormOpen}
            onOpenChange={open => {
              setLocFormOpen(open);
              if (!open) setEditingLocId(null);
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingLocId ? 'Edit Location' : 'New Location'}</DialogTitle>
                <DialogDescription>
                  Locations are shared across events and classes.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Name *</Label>
                  <Input
                    value={locForm.name}
                    onChange={e => setLocForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Fig Tree Market"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Slug *</Label>
                  <Input
                    value={locForm.slug}
                    onChange={e => setLocForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder="fig-tree-market"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Address</Label>
                  <Input
                    value={locForm.address}
                    onChange={e => setLocForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="123 Main St, Bloomington, IN"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Locality</Label>
                  <Select
                    value={locForm.localityId || SELECT_NONE}
                    onValueChange={v =>
                      setLocForm(f => ({ ...f, localityId: v === SELECT_NONE ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select locality" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                      {localities.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editingLocId && (
                  <div className="flex items-center gap-2">
                    <Switch
                      id="loc-active"
                      checked={locForm.isActive}
                      onCheckedChange={v => setLocForm(f => ({ ...f, isActive: v }))}
                    />
                    <Label htmlFor="loc-active">Active</Label>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setLocFormOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    !locForm.name.trim() || !locForm.slug.trim() || saveLocMutation.isPending
                  }
                  onClick={() => saveLocMutation.mutate(locForm)}
                >
                  {saveLocMutation.isPending
                    ? 'Saving…'
                    : editingLocId
                      ? 'Save Changes'
                      : 'Create Location'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Locality</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No locations yet. Create one to use as a venue.
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map(loc => (
                    <TableRow key={loc.id}>
                      <TableCell className="font-medium">{loc.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {loc.localityName ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {loc.address ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={loc.isActive ? 'default' : 'secondary'}>
                          {loc.isActive ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openEditLocation(loc)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── Series tab ─────────────────────────────────────────────────── */}
        <TabsContent value="series" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setSeriesForm(BLANK_SERIES_FORM);
                setSeriesFormOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Recurring Series
            </Button>
          </div>

          {/* Series create dialog */}
          <Dialog open={seriesFormOpen} onOpenChange={setSeriesFormOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Recurring Series</DialogTitle>
                <DialogDescription>
                  Define a recurring event series. Instances will be materialized automatically.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Title *</Label>
                  <Input
                    value={seriesForm.title}
                    onChange={e => setSeriesForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Weekly Yoga Class"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    value={seriesForm.description}
                    onChange={e => setSeriesForm(f => ({ ...f, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Category *</Label>
                    <Select
                      value={seriesForm.category}
                      onValueChange={v => setSeriesForm(f => ({ ...f, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          'community',
                          'fitness',
                          'arts',
                          'music',
                          'food',
                          'wellness',
                          'education',
                          'sports',
                          'social',
                          'outdoor',
                          'technology',
                          'business',
                        ].map(c => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duration (minutes) *</Label>
                    <Input
                      type="number"
                      min={15}
                      step={15}
                      value={seriesForm.durationMinutes}
                      onChange={e =>
                        setSeriesForm(f => ({ ...f, durationMinutes: Number(e.target.value) }))
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>First occurrence (local time) *</Label>
                  <Input
                    type="datetime-local"
                    value={seriesForm.dtstart}
                    onChange={e => setSeriesForm(f => ({ ...f, dtstart: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Wall-clock time in the series timezone. No UTC conversion.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Timezone *</Label>
                  <Select
                    value={seriesForm.timezone}
                    onValueChange={v => setSeriesForm(f => ({ ...f, timezone: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Indiana/Indianapolis">
                        America/Indiana/Indianapolis
                      </SelectItem>
                      <SelectItem value="America/Chicago">America/Chicago</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                      <SelectItem value="America/Denver">America/Denver</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>RRULE *</Label>
                  <Input
                    value={seriesForm.rrule}
                    onChange={e => setSeriesForm(f => ({ ...f, rrule: e.target.value }))}
                    placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR"
                  />
                  <p className="text-xs text-muted-foreground">
                    RFC 5545 RRULE without the DTSTART prefix. Examples:{' '}
                    <code>FREQ=WEEKLY;BYDAY=MO</code>, <code>FREQ=DAILY;INTERVAL=2;COUNT=30</code>
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Locality</Label>
                  <Select
                    value={seriesForm.localityId || SELECT_NONE}
                    onValueChange={v => {
                      const localityId = v === SELECT_NONE ? '' : v;
                      const locality = localities.find(l => l.id === localityId);
                      setSeriesForm(f => ({
                        ...f,
                        localityId,
                        regionId: locality?.regionId ?? '',
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None (region-wide)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NONE}>None</SelectItem>
                      {localities.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>External URL</Label>
                  <Input
                    value={seriesForm.externalUrl}
                    onChange={e => setSeriesForm(f => ({ ...f, externalUrl: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSeriesFormOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => createSeriesMutation.mutate(seriesForm)}
                  disabled={
                    createSeriesMutation.isPending ||
                    !seriesForm.title ||
                    !seriesForm.dtstart ||
                    !seriesForm.rrule
                  }
                >
                  {createSeriesMutation.isPending ? 'Creating…' : 'Create Series'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Locality</TableHead>
                  <TableHead>RRULE</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seriesList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No series yet. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  seriesList.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.title}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.localityName ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm font-mono text-muted-foreground max-w-[200px] truncate">
                        {s.rrule}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            s.status === 'active'
                              ? 'default'
                              : s.status === 'archived'
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {s.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {s.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => publishSeriesMutation.mutate(s.id)}
                              disabled={publishSeriesMutation.isPending}
                              title="Publish"
                            >
                              Publish
                            </Button>
                          )}
                          {s.status !== 'archived' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => materializeSeriesMutation.mutate(s.id)}
                              disabled={materializeSeriesMutation.isPending}
                              title="Materialize instances now"
                            >
                              Materialize
                            </Button>
                          )}
                          {s.status !== 'archived' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSplitTargetOriginalStartsAt(null);
                                setSplitDialog({
                                  open: true,
                                  seriesId: s.id,
                                  seriesTitle: s.title,
                                });
                              }}
                              title="Split — this and future"
                            >
                              Split
                            </Button>
                          )}
                          {s.status !== 'archived' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => archiveSeriesMutation.mutate(s.id)}
                              disabled={archiveSeriesMutation.isPending}
                              title="Archive"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Split dialog */}
          <Dialog
            open={splitDialog.open}
            onOpenChange={open => {
              if (!open) {
                setSplitDialog({ open: false, seriesId: null, seriesTitle: '' });
                setSplitTargetOriginalStartsAt(null);
              }
            }}
          >
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Split series — this and future</DialogTitle>
                <DialogDescription>
                  Choose the first instance that should become a new series. The original series
                  will end just before this occurrence. You can then edit the new series separately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm font-medium text-foreground">{splitDialog.seriesTitle}</p>
                {!splitInstancesData ? (
                  <p className="text-sm text-muted-foreground">Loading instances…</p>
                ) : splitInstancesData.data.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No upcoming instances found in the next 3 months.
                  </p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto border rounded-md p-1">
                    {splitInstancesData.data.map(inst => {
                      const label = new Date(inst.startsAt).toLocaleString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      });
                      const selected = splitTargetOriginalStartsAt === inst.originalStartsAt;
                      return (
                        <button
                          key={inst.id}
                          type="button"
                          onClick={() => setSplitTargetOriginalStartsAt(inst.originalStartsAt)}
                          className={`w-full text-left rounded px-3 py-2 text-sm transition-colors ${
                            selected
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted text-foreground'
                          }`}
                        >
                          {label}
                          {inst.isException && (
                            <span className="ml-2 text-xs opacity-70">(exception)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {splitTargetOriginalStartsAt && (
                  <p className="text-xs text-muted-foreground">
                    Original series will end before{' '}
                    {new Date(splitTargetOriginalStartsAt).toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    . A new series will be created from that date onward.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSplitDialog({ open: false, seriesId: null, seriesTitle: '' });
                    setSplitTargetOriginalStartsAt(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    !splitTargetOriginalStartsAt ||
                    splitSeriesMutation.isPending ||
                    !splitDialog.seriesId
                  }
                  onClick={() => {
                    if (splitDialog.seriesId && splitTargetOriginalStartsAt) {
                      splitSeriesMutation.mutate({
                        seriesId: splitDialog.seriesId,
                        originalStartsAt: splitTargetOriginalStartsAt,
                      });
                    }
                  }}
                >
                  {splitSeriesMutation.isPending ? 'Splitting…' : 'Split series'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ── Submissions tab ──────────────────────────────────────────────── */}
        <TabsContent value="submissions" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {showAllSubmissions ? 'All submissions' : 'Pending review'}
            </p>
            <Button variant="outline" size="sm" onClick={() => setShowAllSubmissions(v => !v)}>
              {showAllSubmissions ? 'Show pending only' : 'Show all'}
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Starts</TableHead>
                  <TableHead>Submitter</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!submissionsData ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : submissionsData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {showAllSubmissions ? 'No submissions yet.' : 'No pending submissions.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  submissionsData.data.map(sub => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium max-w-[180px] truncate">
                        {sub.eventTitle ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground capitalize">
                        {sub.eventCategory ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {sub.eventStartsAt
                          ? new Date(sub.eventStartsAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{sub.submitterName}</div>
                        <div className="text-muted-foreground text-xs">{sub.submitterEmail}</div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(sub.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            sub.reviewDecision === 'approved'
                              ? 'default'
                              : sub.reviewDecision === 'rejected'
                                ? 'destructive'
                                : 'outline'
                          }
                        >
                          {sub.reviewDecision ?? 'pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {!sub.reviewDecision && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              onClick={() => {
                                setReviewNotes('');
                                setReviewDialog({
                                  open: true,
                                  submissionId: sub.id,
                                  decision: 'approve',
                                  eventTitle: sub.eventTitle ?? 'this event',
                                  eventStartsAt: sub.eventStartsAt,
                                  eventEndsAt: sub.eventEndsAt,
                                  eventVenueId: null,
                                  localityId: sub.localityId,
                                  eventId: sub.eventId,
                                });
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReviewNotes('');
                                setReviewDialog({
                                  open: true,
                                  submissionId: sub.id,
                                  decision: 'reject',
                                  eventTitle: sub.eventTitle ?? 'this event',
                                  eventStartsAt: sub.eventStartsAt,
                                  eventEndsAt: sub.eventEndsAt,
                                  eventVenueId: null,
                                  localityId: sub.localityId,
                                  eventId: sub.eventId,
                                });
                              }}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Review dialog */}
          <Dialog
            open={reviewDialog.open}
            onOpenChange={open => {
              if (!open)
                setReviewDialog(d => ({
                  ...d,
                  open: false,
                  eventStartsAt: null,
                  eventEndsAt: null,
                  eventVenueId: null,
                  localityId: null,
                  eventId: null,
                }));
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {reviewDialog.decision === 'approve' ? 'Approve submission' : 'Reject submission'}
                </DialogTitle>
                <DialogDescription>
                  {reviewDialog.decision === 'approve'
                    ? `"${reviewDialog.eventTitle}" will be published to the public calendar and the submitter will be notified.`
                    : `The submitter will be notified that "${reviewDialog.eventTitle}" was not approved.`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {/* Conflict warnings on approval */}
                {reviewDialog.decision === 'approve' && submissionHasVenueConflict && (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 space-y-1">
                    <p className="text-sm font-medium text-destructive">Same-venue conflict</p>
                    {submissionConflictData?.venueConflicts.map(c => (
                      <p key={c.id} className="text-xs text-destructive/80">
                        "{c.title}" at{' '}
                        {new Date(c.startsAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                    ))}
                  </div>
                )}
                {reviewDialog.decision === 'approve' &&
                  !submissionHasVenueConflict &&
                  submissionHasLocalityConflict && (
                    <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 space-y-1">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                        Nearby same-area events
                      </p>
                      {submissionConflictData?.localityConflicts.map(c => (
                        <p key={c.id} className="text-xs text-amber-600 dark:text-amber-500">
                          "{c.title}" at{' '}
                          {new Date(c.startsAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      ))}
                    </div>
                  )}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    Note to submitter{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <Textarea
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    placeholder={
                      reviewDialog.decision === 'approve'
                        ? 'Any message for the submitter…'
                        : 'Reason for rejection (sent to submitter)…'
                    }
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setReviewDialog(d => ({ ...d, open: false }))}
                >
                  Cancel
                </Button>
                <Button
                  variant={reviewDialog.decision === 'reject' ? 'destructive' : 'default'}
                  disabled={
                    approveSubmissionMutation.isPending || rejectSubmissionMutation.isPending
                  }
                  onClick={() => {
                    if (!reviewDialog.submissionId) return;
                    const args = { id: reviewDialog.submissionId, notes: reviewNotes || undefined };
                    if (reviewDialog.decision === 'approve') {
                      approveSubmissionMutation.mutate(args);
                    } else {
                      rejectSubmissionMutation.mutate(args);
                    }
                  }}
                >
                  {approveSubmissionMutation.isPending || rejectSubmissionMutation.isPending
                    ? 'Saving…'
                    : reviewDialog.decision === 'approve'
                      ? 'Approve and publish'
                      : 'Reject submission'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BlogAdminTab() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [view, setView] = useState<'list' | 'edit' | 'writers' | 'categories'>('list');
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [writerSearch, setWriterSearch] = useState('');
  const [writerSearchResult, setWriterSearchResult] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isBlogWriter: boolean;
  } | null>(null);
  const [catName, setCatName] = useState('');
  const [catDesc, setCatDesc] = useState('');

  // Review dialog state
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    postId: string;
    action: 'publish' | 'request-changes' | 'reject';
    postTitle: string;
  } | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');

  function openReviewDialog(
    postId: string,
    postTitle: string,
    action: 'publish' | 'request-changes' | 'reject'
  ) {
    setReviewNote('');
    setScheduledFor('');
    setReviewDialog({ open: true, postId, action, postTitle });
  }

  const emptyForm = {
    title: '',
    excerpt: '',
    content: '',
    featuredImageUrl: '',
    categoryId: '',
    tags: '',
    regionId: '',
    localityId: '',
    seoTitle: '',
    seoDescription: '',
  };
  const [form, setForm] = useState(emptyForm);

  const { data: posts = [], refetch: refetchPosts } = useQuery<BlogPostRow[]>({
    queryKey: ['/api/admin/blog', statusFilter],
    queryFn: () => {
      const url = statusFilter ? `/api/admin/blog?status=${statusFilter}` : '/api/admin/blog';
      return authedJson<BlogPostRow[]>(url);
    },
  });

  const { data: categories = [], refetch: refetchCategories } = useQuery<BlogCategoryRow[]>({
    queryKey: ['/api/blog/categories'],
    queryFn: () => fetchJson<BlogCategoryRow[]>('/api/blog/categories'),
  });

  const { data: writers = [], refetch: refetchWriters } = useQuery<BlogWriterRow[]>({
    queryKey: ['/api/admin/blog/writers'],
    queryFn: () => authedJson<BlogWriterRow[]>('/api/admin/blog/writers'),
    enabled: isAdmin,
  });

  const { data: editPost } = useQuery({
    queryKey: ['/api/admin/blog/post', editingPost],
    queryFn: () => authedJson<any>(`/api/admin/blog/${editingPost}`),
    enabled: !!editingPost,
  });

  React.useEffect(() => {
    if (editPost && editingPost) {
      setForm({
        title: editPost.title ?? '',
        excerpt: editPost.excerpt ?? '',
        content: editPost.content ?? '',
        featuredImageUrl: editPost.featuredImageUrl ?? '',
        categoryId: editPost.categoryId ?? '',
        tags: Array.isArray(editPost.tags) ? editPost.tags.join(', ') : '',
        regionId: editPost.regionId ?? '',
        localityId: editPost.localityId ?? '',
        seoTitle: editPost.seoTitle ?? '',
        seoDescription: editPost.seoDescription ?? '',
      });
    }
  }, [editPost, editingPost]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        tags: form.tags
          ? form.tags
              .split(',')
              .map(t => t.trim())
              .filter(Boolean)
          : [],
        categoryId: form.categoryId || null,
        regionId: form.regionId || null,
        localityId: form.localityId || null,
        featuredImageUrl: form.featuredImageUrl || null,
        seoTitle: form.seoTitle || null,
        seoDescription: form.seoDescription || null,
        excerpt: form.excerpt || null,
      };
      if (editingPost) {
        return apiRequest('PATCH', `/api/admin/blog/${editingPost}`, body);
      }
      return apiRequest('POST', '/api/admin/blog', body);
    },
    onSuccess: () => {
      toast({ title: editingPost ? 'Post saved' : 'Post created' });
      setView('list');
      setEditingPost(null);
      setForm(emptyForm);
      refetchPosts();
    },
    onError: () => toast({ title: 'Failed to save post', variant: 'destructive' }),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      id,
      action,
      note,
      scheduledFor: sf,
    }: {
      id: string;
      action: string;
      note?: string;
      scheduledFor?: string;
    }) => {
      const body: Record<string, unknown> = {};
      if (note) body.note = note;
      if (sf) body.scheduledFor = sf;
      return apiRequest('POST', `/api/admin/blog/${id}/${action}`, body);
    },
    onSuccess: (_, vars) => {
      const label =
        vars.action === 'publish'
          ? vars.scheduledFor
            ? 'Post scheduled'
            : 'Post published'
          : vars.action === 'request-changes'
            ? 'Changes requested — writer notified'
            : vars.action === 'reject'
              ? 'Post rejected'
              : 'Status updated';
      toast({ title: label });
      setReviewDialog(null);
      refetchPosts();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to update status';
      toast({ title: msg, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/blog/${id}`),
    onSuccess: () => {
      toast({ title: 'Post deleted' });
      refetchPosts();
    },
    onError: () => toast({ title: 'Failed to delete post', variant: 'destructive' }),
  });

  const pinMutation = useMutation({
    mutationFn: ({ id, isPinned }: { id: string; isPinned: boolean }) =>
      apiRequest('PATCH', `/api/admin/blog/${id}`, { isPinned }),
    onSuccess: (_, { isPinned }) => {
      toast({ title: isPinned ? 'Post pinned as featured' : 'Post unpinned' });
      refetchPosts();
    },
    onError: () => toast({ title: 'Failed to update pin', variant: 'destructive' }),
  });

  const writerMutation = useMutation({
    mutationFn: ({ userId, isBlogWriter }: { userId: string; isBlogWriter: boolean }) =>
      apiRequest('PATCH', `/api/admin/blog/writers/${userId}`, { isBlogWriter }),
    onSuccess: () => {
      toast({ title: 'Writer access updated' });
      refetchWriters();
    },
    onError: () => toast({ title: 'Failed to update writer access', variant: 'destructive' }),
  });

  const catMutation = useMutation({
    mutationFn: () =>
      apiRequest('POST', '/api/admin/blog/categories', {
        name: catName,
        description: catDesc || undefined,
      }),
    onSuccess: () => {
      toast({ title: 'Category created' });
      setCatName('');
      setCatDesc('');
      refetchCategories();
    },
    onError: () => toast({ title: 'Failed to create category', variant: 'destructive' }),
  });

  const deleteCatMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/blog/categories/${id}`),
    onSuccess: () => {
      toast({ title: 'Category deleted' });
      refetchCategories();
    },
    onError: () => toast({ title: 'Failed to delete category', variant: 'destructive' }),
  });

  async function searchUser() {
    if (!writerSearch) return;
    const res = await fetch(`/api/admin/users?search=${encodeURIComponent(writerSearch)}`);
    if (!res.ok) {
      toast({ title: 'User not found', variant: 'destructive' });
      return;
    }
    const data = await res.json();
    const u = Array.isArray(data) ? data[0] : (data?.data?.[0] ?? data?.users?.[0] ?? data);
    if (!u?.id) {
      toast({ title: 'User not found', variant: 'destructive' });
      return;
    }
    setWriterSearchResult(u);
  }

  if (view === 'edit') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingPost(null);
              setForm(emptyForm);
            }}
          >
            Back to list
          </Button>
          <h2 className="text-xl font-semibold">{editingPost ? 'Edit Post' : 'New Post'}</h2>
        </div>
        <div className="grid gap-4">
          <div>
            <Label htmlFor="blog-title">Title</Label>
            <Input
              id="blog-title"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Post title"
            />
          </div>
          <div>
            <Label htmlFor="blog-excerpt">Excerpt (optional)</Label>
            <Textarea
              id="blog-excerpt"
              value={form.excerpt}
              onChange={e => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              placeholder="Short description shown in post previews"
            />
          </div>
          <div>
            <Label>Content</Label>
            <BlogEditor
              value={form.content}
              onChange={html => setForm({ ...form, content: html })}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="blog-image">Featured Image URL (optional)</Label>
              <Input
                id="blog-image"
                value={form.featuredImageUrl}
                onChange={e => setForm({ ...form, featuredImageUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label htmlFor="blog-category">Category</Label>
              <Select
                value={form.categoryId || SELECT_NONE}
                onValueChange={v => setForm({ ...form, categoryId: v === SELECT_NONE ? '' : v })}
              >
                <SelectTrigger id="blog-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SELECT_NONE}>None</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="blog-tags">Tags (comma separated)</Label>
            <Input
              id="blog-tags"
              value={form.tags}
              onChange={e => setForm({ ...form, tags: e.target.value })}
              placeholder="events, community, wellness"
            />
          </div>
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <p className="text-sm font-medium text-muted-foreground">SEO (optional)</p>
            <Input
              value={form.seoTitle}
              onChange={e => setForm({ ...form, seoTitle: e.target.value })}
              placeholder="SEO Title (defaults to post title)"
            />
            <Textarea
              value={form.seoDescription}
              onChange={e => setForm({ ...form, seoDescription: e.target.value })}
              rows={2}
              placeholder="SEO Description (defaults to excerpt)"
            />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !form.title || !form.content}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
          {editingPost && (editPost?.status === 'draft' || editPost?.status === 'rejected') && (
            <Button
              variant="outline"
              onClick={() => statusMutation.mutate({ id: editingPost, action: 'submit' })}
              disabled={statusMutation.isPending}
            >
              <Send className="h-4 w-4 mr-1" />
              Submit for Review
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingPost(null);
              setForm(emptyForm);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'writers' && isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setView('list')}>
            Back
          </Button>
          <h2 className="text-xl font-semibold">Blog Writer Access</h2>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Find a user</CardTitle>
            <CardDescription>Search by email to grant or revoke blog writer access</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="user@example.com"
                value={writerSearch}
                onChange={e => setWriterSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchUser()}
              />
              <Button onClick={searchUser}>Search</Button>
            </div>
            {writerSearchResult && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">
                    {writerSearchResult.firstName} {writerSearchResult.lastName}
                  </p>
                  <p className="text-sm text-muted-foreground">{writerSearchResult.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={writerSearchResult.isBlogWriter ? 'default' : 'secondary'}>
                    {writerSearchResult.isBlogWriter ? 'Writer' : 'No access'}
                  </Badge>
                  <Switch
                    checked={writerSearchResult.isBlogWriter}
                    onCheckedChange={checked => {
                      writerMutation.mutate({
                        userId: writerSearchResult.id,
                        isBlogWriter: checked,
                      });
                      setWriterSearchResult({ ...writerSearchResult, isBlogWriter: checked });
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Current blog writers</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Writer Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {writers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No writers yet
                    </TableCell>
                  </TableRow>
                ) : (
                  writers.map(w => (
                    <TableRow key={w.id}>
                      <TableCell className="font-medium">
                        {w.firstName} {w.lastName}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{w.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{w.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={w.isBlogWriter}
                          onCheckedChange={checked =>
                            writerMutation.mutate({ userId: w.id, isBlogWriter: checked })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (view === 'categories' && isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setView('list')}>
            Back
          </Button>
          <h2 className="text-xl font-semibold">Blog Categories</h2>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>New Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={catName}
              onChange={e => setCatName(e.target.value)}
              placeholder="Category name"
            />
            <Input
              value={catDesc}
              onChange={e => setCatDesc(e.target.value)}
              placeholder="Description (optional)"
            />
            <Button
              onClick={() => catMutation.mutate()}
              disabled={catMutation.isPending || !catName}
            >
              {catMutation.isPending ? 'Creating…' : 'Create Category'}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No categories yet
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.slug}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteCatMutation.mutate(c.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Blog Posts</h2>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => setView('writers')}>
                Writer Access
              </Button>
              <Button variant="outline" size="sm" onClick={() => setView('categories')}>
                Categories
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={() => {
              setEditingPost(null);
              setForm(emptyForm);
              setView('edit');
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Post
          </Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Select
          value={statusFilter || SELECT_ALL}
          onValueChange={value => setStatusFilter(value === SELECT_ALL ? '' : value)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL}>All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => refetchPosts()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No posts yet. Create your first one!
                  </TableCell>
                </TableRow>
              ) : (
                posts.map(post => {
                  const statusInfo = STATUS_LABELS[post.status] ?? {
                    label: post.status,
                    variant: 'secondary' as const,
                  };
                  return (
                    <TableRow key={post.id}>
                      <TableCell>
                        <p className="font-medium line-clamp-1">{post.title}</p>
                        <p className="text-xs text-muted-foreground">/updates/{post.slug}</p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {post.authorFirstName} {post.authorLastName}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                          {post.isPinned && (
                            <span title="Featured article" className="text-primary">
                              <Pin className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateCompact(post.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingPost(post.id);
                              setView('edit');
                            }}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {['draft', 'rejected', 'changes_requested'].includes(post.status) &&
                            post.authorId === user?.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Submit for review"
                                onClick={() =>
                                  statusMutation.mutate({ id: post.id, action: 'submit' })
                                }
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                          {isAdmin && post.status === 'published' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                post.isPinned ? 'Unpin featured article' : 'Pin as featured article'
                              }
                              onClick={() =>
                                pinMutation.mutate({ id: post.id, isPinned: !post.isPinned })
                              }
                            >
                              {post.isPinned ? (
                                <PinOff className="h-4 w-4 text-primary" />
                              ) : (
                                <Pin className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          )}
                          {isAdmin && post.status === 'pending_review' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Approve"
                                onClick={() => openReviewDialog(post.id, post.title, 'publish')}
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Request changes"
                                onClick={() =>
                                  openReviewDialog(post.id, post.title, 'request-changes')
                                }
                              >
                                <RefreshCw className="h-4 w-4 text-amber-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Reject"
                                onClick={() => openReviewDialog(post.id, post.title, 'reject')}
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                          {isAdmin && post.status === 'published' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Unpublish"
                              onClick={() =>
                                statusMutation.mutate({ id: post.id, action: 'unpublish' })
                              }
                            >
                              <X className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                          {(post.status === 'draft' || isAdmin) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => {
                                if (window.confirm('Delete this post?'))
                                  deleteMutation.mutate(post.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Review dialog — approve/schedule, request changes, reject */}
      {reviewDialog?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold">
              {reviewDialog.action === 'publish'
                ? 'Approve post'
                : reviewDialog.action === 'request-changes'
                  ? 'Request changes'
                  : 'Reject post'}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              &ldquo;{reviewDialog.postTitle}&rdquo;
            </p>

            {reviewDialog.action === 'publish' && (
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={() =>
                    statusMutation.mutate({ id: reviewDialog.postId, action: 'publish' })
                  }
                  disabled={statusMutation.isPending}
                >
                  Publish now
                </Button>
                <div className="space-y-1.5">
                  <label htmlFor="schedule-datetime" className="text-sm font-medium">
                    Or schedule for a future date
                  </label>
                  <input
                    id="schedule-datetime"
                    type="datetime-local"
                    title="Schedule publish date and time"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={scheduledFor}
                    onChange={e => setScheduledFor(e.target.value)}
                  />
                  {scheduledFor && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        statusMutation.mutate({
                          id: reviewDialog.postId,
                          action: 'publish',
                          scheduledFor,
                        })
                      }
                      disabled={statusMutation.isPending}
                    >
                      Schedule for this time
                    </Button>
                  )}
                </div>
              </div>
            )}

            {(reviewDialog.action === 'request-changes' || reviewDialog.action === 'reject') && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  {reviewDialog.action === 'request-changes'
                    ? 'Describe the changes needed *'
                    : 'Reason for rejection *'}
                </label>
                <Textarea
                  rows={4}
                  placeholder={
                    reviewDialog.action === 'request-changes'
                      ? 'Explain what needs to be changed before this can be published…'
                      : 'Explain why this post cannot be published…'
                  }
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                />
                <Button
                  className="w-full"
                  variant={reviewDialog.action === 'reject' ? 'destructive' : 'default'}
                  disabled={!reviewNote.trim() || statusMutation.isPending}
                  onClick={() =>
                    statusMutation.mutate({
                      id: reviewDialog.postId,
                      action: reviewDialog.action,
                      note: reviewNote,
                    })
                  }
                >
                  {reviewDialog.action === 'request-changes'
                    ? 'Send back to writer'
                    : 'Reject post'}
                </Button>
              </div>
            )}

            <Button variant="ghost" className="w-full" onClick={() => setReviewDialog(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface KillSwitchStatus {
  paidCheckoutDisabled: boolean;
  zeroDollarCheckoutDisabled: boolean;
  payoutReleaseDisabled: boolean;
  noncriticalEmailDisabled: boolean;
  creatorApprovalDisabled: boolean;
  publicWriteDisabled: boolean;
}

const KILL_SWITCH_METADATA: {
  key: keyof KillSwitchStatus;
  label: string;
  envVar: string;
  description: string;
}[] = [
  {
    key: 'paidCheckoutDisabled',
    label: 'Paid Checkout',
    envVar: 'KILL_SWITCH_DISABLE_PAID_CHECKOUT',
    description:
      'Blocks new paid checkout sessions. Existing orders, account pages, and read access are unaffected.',
  },
  {
    key: 'zeroDollarCheckoutDisabled',
    label: 'Zero-Dollar Checkout',
    envVar: 'KILL_SWITCH_DISABLE_ZERO_DOLLAR_CHECKOUT',
    description:
      'Blocks the free/zero-dollar completion path. Use if mispricing allows free enrollment on paid classes.',
  },
  {
    key: 'payoutReleaseDisabled',
    label: 'Payout Release',
    envVar: 'KILL_SWITCH_DISABLE_PAYOUT_RELEASE',
    description:
      'Stops automatic payout transfer jobs. Creator earnings visibility and dashboard are unaffected.',
  },
  {
    key: 'noncriticalEmailDisabled',
    label: 'Non-Critical Emails',
    envVar: 'KILL_SWITCH_DISABLE_NONCRITICAL_EMAIL',
    description:
      'Suppresses reminders, follow-ups, and notifications. Password reset and financial emails (receipts, refund notices) still send.',
  },
  {
    key: 'creatorApprovalDisabled',
    label: 'Creator Approvals',
    envVar: 'KILL_SWITCH_DISABLE_CREATOR_APPROVALS',
    description:
      'Prevents approving new creator applications. Submissions and rejections still work.',
  },
  {
    key: 'publicWriteDisabled',
    label: 'Public Writes',
    envVar: 'KILL_SWITCH_DISABLE_PUBLIC_WRITES',
    description:
      'Blocks forum posts, newsletter signups, partner applications, and community reports. Auth and checkout are unaffected.',
  },
];

// ─── Fee profiles (payment splits) ───────────────────────────────────────────

interface FeeProfileRow {
  id: string;
  scope: 'global' | 'creator' | 'class';
  scopeId: string | null;
  classSaleCreatorPct: string;
  classSaleVenuePct: string;
  tipCreatorPct: string;
  venuePayoutMode: 'pct' | 'fixed';
  venueFixedCentsPerSession: number | null;
  payoutDelayHours: number;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

interface FeeProfileFormState {
  scope: 'global' | 'creator' | 'class';
  scopeId: string;
  creatorPct: string;
  venuePct: string;
  venuePayoutMode: 'pct' | 'fixed';
  venueFixedDollars: string;
  payoutDelayHours: string;
}

const emptyFeeProfileForm: FeeProfileFormState = {
  scope: 'class',
  scopeId: '',
  creatorPct: '90',
  venuePct: '0',
  venuePayoutMode: 'pct',
  venueFixedDollars: '0',
  payoutDelayHours: '48',
};

function FeeProfilesTab() {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [form, setForm] = useState<FeeProfileFormState>(emptyFeeProfileForm);

  const { data: profiles = [], refetch } = useQuery<FeeProfileRow[]>({
    queryKey: ['/api/admin/financials/fee-profiles'],
    queryFn: async () => {
      const data = await authedJson<FeeProfileRow[]>('/api/admin/financials/fee-profiles');
      return Array.isArray(data) ? data : [];
    },
  });

  const { data: classesPage } = useQuery<PaginatedResponse<Class>>({
    queryKey: ['/api/admin/classes', { limit: 100 }],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/classes?limit=100');
      return (await response.json()) as PaginatedResponse<Class>;
    },
  });
  const classes = classesPage?.data ?? [];

  const { data: creatorsPage } = useQuery<PaginatedResponse<AdminCreatorRow>>({
    queryKey: ['/api/admin/creators'],
  });
  const creators = creatorsPage?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (data: FeeProfileFormState) =>
      apiRequest('POST', '/api/admin/financials/fee-profiles', {
        scope: data.scope,
        scopeId: data.scope === 'global' ? null : data.scopeId,
        classSaleCreatorPct: Number(data.creatorPct),
        classSaleVenuePct: data.venuePayoutMode === 'fixed' ? 0 : Number(data.venuePct),
        venuePayoutMode: data.venuePayoutMode,
        venueFixedCentsPerSession:
          data.venuePayoutMode === 'fixed'
            ? Math.round(Number(data.venueFixedDollars) * 100)
            : null,
        payoutDelayHours: Number(data.payoutDelayHours) || 48,
      }),
    onSuccess: () => {
      toast({ title: 'Fee profile created' });
      setShowForm(false);
      setForm(emptyFeeProfileForm);
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FeeProfileFormState }) =>
      apiRequest('PUT', `/api/admin/financials/fee-profiles/${id}`, {
        classSaleCreatorPct: Number(data.creatorPct),
        classSaleVenuePct: data.venuePayoutMode === 'fixed' ? 0 : Number(data.venuePct),
        venuePayoutMode: data.venuePayoutMode,
        venueFixedCentsPerSession:
          data.venuePayoutMode === 'fixed'
            ? Math.round(Number(data.venueFixedDollars) * 100)
            : null,
        payoutDelayHours: Number(data.payoutDelayHours) || 48,
      }),
    onSuccess: () => {
      toast({ title: 'Fee profile updated' });
      setShowForm(false);
      setEditingProfileId(null);
      setForm(emptyFeeProfileForm);
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/financials/fee-profiles/${id}`),
    onSuccess: () => {
      toast({ title: 'Fee profile deleted' });
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  function scopeTargetLabel(profile: FeeProfileRow): string {
    if (profile.scope === 'global') return 'All classes';
    if (!profile.scopeId) return '—';
    if (profile.scope === 'class') {
      const cls = classes.find(c => c.id === profile.scopeId);
      return cls ? cls.title : profile.scopeId;
    }
    const creator = creators.find(c => c.id === profile.scopeId);
    if (!creator) return profile.scopeId;
    return (
      creator.displayName ||
      `${creator.userFirstName ?? ''} ${creator.userLastName ?? ''}`.trim() ||
      creator.userEmail ||
      profile.scopeId
    );
  }

  const creatorPctNum = Number(form.creatorPct) || 0;
  const venuePctNum = Number(form.venuePct) || 0;
  const kalovatePct = 100 - creatorPctNum - venuePctNum;
  const overAllocated = kalovatePct < 0;
  // Kalovate absorbs Stripe processing fees (~2.9% + 30¢), so a remainder
  // below ~4% likely loses money on small-ticket sales.
  const thinMargin = !overAllocated && kalovatePct < 4;

  function openEditProfile(profile: FeeProfileRow) {
    setEditingProfileId(profile.id);
    setForm({
      scope: profile.scope,
      scopeId: profile.scopeId ?? '',
      creatorPct: profile.classSaleCreatorPct,
      venuePct: profile.classSaleVenuePct,
      venuePayoutMode: profile.venuePayoutMode,
      venueFixedDollars: String((profile.venueFixedCentsPerSession ?? 0) / 100),
      payoutDelayHours: String(profile.payoutDelayHours),
    });
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (overAllocated) return;
    if (editingProfileId) {
      updateMutation.mutate({ id: editingProfileId, data: form });
      return;
    }
    if (form.scope !== 'global' && !form.scopeId) {
      toast({
        title: 'Pick a target',
        description: `Choose which ${form.scope} this profile applies to.`,
        variant: 'destructive',
      });
      return;
    }
    createMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Payment Splits</h2>
          <p className="text-sm text-muted-foreground">
            Fee profiles control how each class sale is split between the creator, the venue, and
            Kalovate. The most specific active profile wins: class &gt; creator &gt; global.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingProfileId(null);
            setForm(emptyFeeProfileForm);
            setShowForm(true);
          }}
          data-testid="button-add-fee-profile"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Profile
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingProfileId ? 'Edit Fee Profile' : 'New Fee Profile'}</CardTitle>
            <CardDescription>
              {editingProfileId
                ? "Scope and target can't be changed here — delete and recreate to move a profile to a different class or creator."
                : 'Kalovate keeps whatever is left after the creator and venue shares, and absorbs Stripe processing fees (~2.9% + 30¢) out of that remainder.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select
                    value={form.scope}
                    disabled={!!editingProfileId}
                    onValueChange={(value: 'global' | 'creator' | 'class') =>
                      setForm(f => ({ ...f, scope: value, scopeId: '' }))
                    }
                  >
                    <SelectTrigger data-testid="select-fee-profile-scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="class">Single class</SelectItem>
                      <SelectItem value="creator">All of a creator's classes</SelectItem>
                      <SelectItem value="global">Global default</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.scope !== 'global' && (
                  <div className="space-y-2">
                    <Label>{form.scope === 'class' ? 'Class' : 'Creator'}</Label>
                    <Select
                      value={form.scopeId || SELECT_NONE}
                      disabled={!!editingProfileId}
                      onValueChange={value =>
                        setForm(f => ({ ...f, scopeId: value === SELECT_NONE ? '' : value }))
                      }
                    >
                      <SelectTrigger data-testid="select-fee-profile-target">
                        <SelectValue
                          placeholder={form.scope === 'class' ? 'Pick a class' : 'Pick a creator'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NONE}>
                          {form.scope === 'class' ? 'Pick a class' : 'Pick a creator'}
                        </SelectItem>
                        {form.scope === 'class'
                          ? classes.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.title}
                              </SelectItem>
                            ))
                          : creators
                              .filter(c => c.id)
                              .map(c => (
                                <SelectItem key={c.id!} value={c.id!}>
                                  {c.displayName ||
                                    `${c.userFirstName ?? ''} ${c.userLastName ?? ''}`.trim() ||
                                    c.userEmail}
                                </SelectItem>
                              ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="fee-creator-pct">Creator %</Label>
                  <Input
                    id="fee-creator-pct"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.creatorPct}
                    onChange={e => setForm(f => ({ ...f, creatorPct: e.target.value }))}
                    data-testid="input-fee-creator-pct"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fee-venue-mode">Venue payout</Label>
                  <select
                    id="fee-venue-mode"
                    aria-label="Venue payout mode"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={form.venuePayoutMode}
                    onChange={e =>
                      setForm(f => ({ ...f, venuePayoutMode: e.target.value as 'pct' | 'fixed' }))
                    }
                  >
                    <option value="pct">% of sales</option>
                    <option value="fixed">Fixed $ / session (rental)</option>
                  </select>
                </div>
                {form.venuePayoutMode === 'pct' ? (
                  <div className="space-y-2">
                    <Label htmlFor="fee-venue-pct">Venue %</Label>
                    <Input
                      id="fee-venue-pct"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={form.venuePct}
                      onChange={e => setForm(f => ({ ...f, venuePct: e.target.value }))}
                      data-testid="input-fee-venue-pct"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="fee-venue-fixed">Venue $ / session</Label>
                    <Input
                      id="fee-venue-fixed"
                      type="number"
                      min="0"
                      step="1"
                      value={form.venueFixedDollars}
                      onChange={e => setForm(f => ({ ...f, venueFixedDollars: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      A per-session rental, funded from the creator's share across that session's
                      tickets.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="fee-payout-delay">Payout delay (hours)</Label>
                  <Input
                    id="fee-payout-delay"
                    type="number"
                    min="0"
                    value={form.payoutDelayHours}
                    onChange={e => setForm(f => ({ ...f, payoutDelayHours: e.target.value }))}
                    data-testid="input-fee-payout-delay"
                  />
                </div>
              </div>

              <p
                className={`text-sm ${
                  overAllocated
                    ? 'text-destructive font-medium'
                    : thinMargin
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-muted-foreground'
                }`}
                data-testid="text-kalovate-remainder"
              >
                {overAllocated
                  ? `Creator + venue is ${creatorPctNum + venuePctNum}% — splits cannot exceed 100%.`
                  : `Kalovate keeps ${kalovatePct.toFixed(1)}%${
                      thinMargin
                        ? ' — warning: this may not cover Stripe processing fees (~2.9% + 30¢).'
                        : '.'
                    }`}
              </p>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending || overAllocated}
                  data-testid="button-save-fee-profile"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingProfileId
                      ? 'Save Changes'
                      : 'Create Profile'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingProfileId(null);
                    setForm(emptyFeeProfileForm);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active Profiles</CardTitle>
          <CardDescription>
            Newer profiles take precedence within the same scope. Without any profile, the global
            default is 90% creator / 0% venue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No fee profiles yet — all sales use the built-in default (90% creator, 0% venue).
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead className="text-right">Creator</TableHead>
                  <TableHead className="text-right">Venue</TableHead>
                  <TableHead className="text-right">Kalovate</TableHead>
                  <TableHead className="text-right">Delay</TableHead>
                  <TableHead>Effective</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map(profile => {
                  const cPct = Number(profile.classSaleCreatorPct);
                  const vPct = Number(profile.classSaleVenuePct);
                  const ended =
                    profile.effectiveUntil && new Date(profile.effectiveUntil) < new Date();
                  return (
                    <TableRow key={profile.id} className={ended ? 'opacity-50' : undefined}>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {profile.scope}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[16rem] truncate">
                        {scopeTargetLabel(profile)}
                      </TableCell>
                      <TableCell className="text-right">{cPct}%</TableCell>
                      <TableCell className="text-right">{vPct}%</TableCell>
                      <TableCell className="text-right">
                        {(100 - cPct - vPct).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right">{profile.payoutDelayHours}h</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDateCompact(profile.effectiveFrom)}
                        {profile.effectiveUntil
                          ? ` – ${formatDateCompact(profile.effectiveUntil)}`
                          : ''}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditProfile(profile)}
                            data-testid={`button-edit-fee-profile-${profile.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(profile.id)}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-delete-fee-profile-${profile.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PayoutJobsCard />
    </div>
  );
}

interface PayoutJobRow {
  id: string;
  creatorId: string | null;
  creatorDisplayName: string | null;
  payeeType: 'creator' | 'venue_owner';
  payeeId: string | null;
  orderId: string;
  eligibleAt: string;
  status: 'scheduled' | 'blocked' | 'released' | 'reversed' | 'failed';
  stripeTransferId: string | null;
  holdReason: string | null;
  creatorEarningsCents: number | null;
  venueEarningsCents: number | null;
}

type PayoutAction = 'hold' | 'retry' | 'mark-failed';

const PAYOUT_ACTION_LABELS: Record<PayoutAction, string> = {
  hold: 'Hold payout',
  retry: 'Retry payout',
  'mark-failed': 'Mark payout failed',
};

const PAYOUT_STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    scheduled: 'secondary',
    released: 'default',
    blocked: 'destructive',
    failed: 'destructive',
    reversed: 'outline',
  };

function PayoutJobsCard() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingAction, setPendingAction] = useState<{
    job: PayoutJobRow;
    action: PayoutAction;
  } | null>(null);
  const [actionReason, setActionReason] = useState('');

  const { data: jobs = [], refetch } = useQuery<PayoutJobRow[]>({
    queryKey: ['/api/admin/financials/payouts', statusFilter],
    queryFn: async () => {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const data = await authedJson<PayoutJobRow[]>(`/api/admin/financials/payouts${params}`);
      return Array.isArray(data) ? data : [];
    },
  });

  const actionMutation = useMutation({
    mutationFn: ({
      job,
      action,
      reason,
    }: {
      job: PayoutJobRow;
      action: PayoutAction;
      reason: string;
    }) => apiRequest('POST', `/api/admin/financials/payouts/${job.id}/${action}`, { reason }),
    onSuccess: (_data, vars) => {
      toast({ title: PAYOUT_ACTION_LABELS[vars.action], description: 'Done.' });
      setPendingAction(null);
      setActionReason('');
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  function amountCents(job: PayoutJobRow): number {
    return (
      (job.payeeType === 'venue_owner' ? job.venueEarningsCents : job.creatorEarningsCents) ?? 0
    );
  }

  function payeeLabel(job: PayoutJobRow): string {
    if (job.payeeType === 'venue_owner') return 'Venue owner';
    return job.creatorDisplayName ?? 'Creator';
  }

  function actionsFor(job: PayoutJobRow): PayoutAction[] {
    switch (job.status) {
      case 'scheduled':
        return ['hold', 'mark-failed'];
      case 'blocked':
        return ['retry', 'mark-failed'];
      case 'failed':
        return ['retry'];
      default:
        return [];
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Payout Jobs</CardTitle>
            <CardDescription>
              Scheduled payouts release after the hold window (QStash + hourly sweep backstop).
              Blocked and failed jobs need admin action — retry reschedules them for the next sweep.
            </CardDescription>
          </div>
          <Select
            value={statusFilter || SELECT_ALL}
            onValueChange={value => setStatusFilter(value === SELECT_ALL ? '' : value)}
          >
            <SelectTrigger className="w-40" data-testid="select-payout-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_ALL}>All statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="released">Released</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payout jobs.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payee</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Eligible</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Note</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map(job => (
                <TableRow key={job.id}>
                  <TableCell>
                    <div className="font-medium">{payeeLabel(job)}</div>
                    <div className="text-xs text-muted-foreground">
                      Order {job.orderId.slice(0, 8)}…
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    ${(amountCents(job) / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {formatDateCompact(job.eligibleAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={PAYOUT_STATUS_VARIANTS[job.status] ?? 'outline'}>
                      {job.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[14rem] truncate text-xs text-muted-foreground">
                    {job.holdReason ??
                      (job.stripeTransferId ? `Transfer ${job.stripeTransferId}` : '—')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {actionsFor(job).map(action => (
                        <Button
                          key={action}
                          variant={action === 'retry' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setPendingAction({ job, action });
                            setActionReason('');
                          }}
                          data-testid={`button-payout-${action}-${job.id}`}
                        >
                          {action === 'hold' ? 'Hold' : action === 'retry' ? 'Retry' : 'Fail'}
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog
        open={!!pendingAction}
        onOpenChange={open => {
          if (!open) setPendingAction(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingAction ? PAYOUT_ACTION_LABELS[pendingAction.action] : ''}
            </DialogTitle>
            <DialogDescription>
              {pendingAction
                ? `${payeeLabel(pendingAction.job)} — $${(amountCents(pendingAction.job) / 100).toFixed(2)}. A reason is required for the audit log (min 10 characters).`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={actionReason}
            onChange={e => setActionReason(e.target.value)}
            placeholder="Why are you taking this action?"
            rows={3}
            data-testid="input-payout-action-reason"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPendingAction(null)}>
              Cancel
            </Button>
            <Button
              disabled={actionReason.trim().length < 10 || actionMutation.isPending}
              onClick={() =>
                pendingAction &&
                actionMutation.mutate({ ...pendingAction, reason: actionReason.trim() })
              }
              data-testid="button-confirm-payout-action"
            >
              {actionMutation.isPending ? 'Working…' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function KillSwitchesTab() {
  const {
    data: status,
    isLoading,
    isError,
    refetch,
  } = useQuery<KillSwitchStatus>({
    queryKey: ['/api/admin/kill-switches'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/kill-switches');
      return res.json() as Promise<KillSwitchStatus>;
    },
    refetchInterval: 30_000,
  });

  const activeCount = status ? KILL_SWITCH_METADATA.filter(m => status[m.key]).length : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Kill Switches</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Environment-backed. Toggle by setting the env var to{' '}
            <code className="font-mono bg-muted px-1 rounded text-xs">true</code> in Doppler or
            Vercel. Changes take effect immediately — no redeploy needed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {activeCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>
            <strong>
              {activeCount} kill switch{activeCount !== 1 ? 'es' : ''} active.
            </strong>{' '}
            Affected flows are currently blocked.
          </span>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}

      {isError && (
        <div className="rounded-2xl border border-border/70 bg-card p-8 text-center">
          <p className="text-sm text-destructive mb-3">Failed to load kill switch status.</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}

      {status && (
        <div className="grid gap-3">
          {KILL_SWITCH_METADATA.map(({ key, label, envVar, description }) => {
            const active = status[key];
            return (
              <div
                key={key}
                className={`rounded-xl border px-5 py-4 flex items-start justify-between gap-4 ${active ? 'border-destructive/40 bg-destructive/5' : 'border-border/70 bg-card'}`}
              >
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{label}</span>
                    <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                      {envVar}=true
                    </code>
                  </div>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
                <Badge variant={active ? 'destructive' : 'secondary'} className="shrink-0 mt-0.5">
                  {active ? 'ACTIVE' : 'Off'}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
